import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminLinkAction,
  AdminMainText,
  AdminMetadataText,
  AdminMetric,
  AdminMetricDelta,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowActionsButton,
  AdminSearchField,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  AdminDetailTableHeader,
  AdminFlagStatusBadge,
  adminFormInputClasses,
  adminFormSelectClasses,
  adminFormTextareaClasses,
  isEscalatedAuditLogEntry,
  getAdminNoteAddedByDisplay,
  deriveMessageThreadStatus,
  normalizeMessageStatus,
  normalizeMessageDeliveryStatus,
  MessageDeliveryTicks,
  normalizeQuoteStatus,
  normalizeQuoteThreadDeliveryStatus,
  QUOTE_DISPLAY_STATUSES,
  ADMIN_TAB_CONTENT_CONTAINER_CLASS,
  ADMIN_TAB_CONTENT_OFFSET_CLASS,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  ADMIN_TAB_CONTENT_WIDE_CLASS,
  ADMIN_METRIC_PANEL_CLASS,
} from '../shared/adminUx';

import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import {
  CONFIG_LIST_PAGE_SIZE,
  getDetailTablePagination,
} from '../../../utils/configurationTableUx';
import { getMessageScenarioByConversationId } from '../../../data/messageModerationScenarios';
import { getQuoteScenarioByQuoteId, getQuoteRequestSourceDisplay } from '../../../data/quoteModerationScenarios';
import {
  getThreadAuditLogRows,
  syncLocalAuditLogsToChangeHistory,
} from '../../../data/changeHistoryAudit';

/** Shared Messages / Quote Activity chat layout (both tabs use renderTimeline). */
const ADMIN_CHAT_THREAD_LAYOUT = {
  viewportHeight: 'calc(100vh - 360px)',
  tabContentClassName: ADMIN_TAB_CONTENT_WIDE_CLASS,
  messageRowClassName: (sender) =>
    `flex max-w-[49%] ${sender === 'business' ? 'flex-row-reverse' : 'flex-row'} items-start gap-2`,
  bubbleClassName: (sender) =>
    `px-4 py-2 rounded-xl ${
      sender === 'business'
        ? 'bg-primaryblue text-white rounded-tr-md'
        : 'bg-mistygray text-offblack rounded-tl-md'
    }`,
};

/** Flag ID | Case ID | Message/Quote Thread ID | Flagged By | Reason | Severity | Case Status | Flagged On | Action */
const MESSAGE_FLAGS_TABLE_GRID =
  'minmax(0, 0.7fr) minmax(0, 0.9fr) minmax(0, 0.85fr) minmax(0, 0.95fr) minmax(0, 1.2fr) minmax(0, 0.5fr) minmax(0, 0.85fr) minmax(0, 1fr) minmax(0, 0.85fr)';

/** Audit Log tab — Date and Time · Event · Details · Related Record */
const AUDIT_LOG_GRID = 'minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 1.8fr) minmax(0, 1.1fr)';

const isQuoteThreadType = (type = '') => String(type).toLowerCase().includes('quote');

const resolveThreadRef = (thread) => {
  if (!thread) return null;
  if (isQuoteThreadType(thread.type || thread.threadType)) {
    return thread.quoteId || thread.threadId || thread.conversationId || null;
  }
  return thread.messageId || thread.threadId || thread.conversationId || null;
};

const formatAuditDateTime = (value) => {
  if (!value) return '—';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  const asDate = new Date(value);
  if (!Number.isNaN(asDate.getTime())) {
    return asDate.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return String(value);
};

const normalizeThreadAuditLog = (log, threadRef) => {
  if (!log) return null;
  const event =
    log.event ||
    log.action ||
    (log.detailSummary ? String(log.detailSummary).split(/[:.]/)[0].trim() : null) ||
    'Activity';
  const details =
    log.details ||
    [log.performedBy, log.detailSummary || log.summary].filter(Boolean).join(' · ') ||
    '—';
  return {
    id: log.id || log.changeId,
    changeId: log.changeId,
    dateTime: log.dateTime || log.timestamp || log.changedOn || null,
    event,
    details,
    relatedRecord: log.relatedRecord || log.record || threadRef || '—',
    performedBy: log.performedBy || log.changedBy || null,
    source: log.source || null,
    outcome: log.outcome || null,
    timestamp: log.timestamp || log.dateTime || null,
    detailSummary: log.detailSummary || log.details || null,
  };
};

const mapCentralThreadAudit = (rows) =>
  (rows || []).map((row) => ({
    id: row.changeId,
    changeId: row.changeId,
    dateTime: row.timestamp ? new Date(row.timestamp) : row.dateTime,
    event: row.event,
    details: row.details,
    relatedRecord: row.relatedRecord || '—',
  }));

const refreshThreadAuditFromStore = (threadRef, isQuote, fallbackLogs = []) => {
  if (!threadRef) return (fallbackLogs || []).map((log) => normalizeThreadAuditLog(log, threadRef)).filter(Boolean);
  const module = isQuote ? 'Quotes' : 'Messages';
  const sourceModuleRef = isQuote ? 'quotes' : 'messages';
  syncLocalAuditLogsToChangeHistory({
    logs: (fallbackLogs || []).map((log) => ({
      ...log,
      action: log.event || log.action,
      event: log.event || log.action,
      details: log.details || log.detailSummary,
      performedBy: log.performedBy,
      timestamp: log.dateTime || log.timestamp,
      relatedRecord: log.relatedRecord || threadRef,
    })),
    module,
    sourceModuleRef,
    sourceItemRef: threadRef,
    changedItem: threadRef,
    itemId: threadRef,
  });
  const central = mapCentralThreadAudit(getThreadAuditLogRows(threadRef, { isQuote }));
  if (central.length) return central;
  return (fallbackLogs || []).map((log) => normalizeThreadAuditLog(log, threadRef)).filter(Boolean);
};

const buildDefaultThreadAuditLogs = (threadRef, isQuote) => {
  const stamp = (daysAgo, hour, minute) => {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    d.setHours(hour, minute, 0, 0);
    return d.toISOString();
  };
  const ref = threadRef || (isQuote ? 'Q-0000' : 'THR-0000');
  if (isQuote) {
    return [
      {
        id: 'LOG-Q1',
        dateTime: stamp(5, 10, 15),
        event: 'Quote submitted',
        details: `Customer submitted quote ${ref}`,
        relatedRecord: ref,
        performedBy: 'Customer',
        source: 'Quotes',
        outcome: 'Success',
      },
      {
        id: 'LOG-Q2',
        dateTime: stamp(4, 14, 20),
        event: 'Business replied',
        details: 'Business sent a quote thread reply',
        relatedRecord: ref,
        performedBy: 'Business',
        source: 'Quotes',
        outcome: 'Success',
      },
      {
        id: 'LOG-Q3',
        dateTime: stamp(2, 11, 5),
        event: 'Admin viewed',
        details: `Admin viewed quote thread ${ref}`,
        relatedRecord: ref,
        performedBy: 'Admin',
        source: 'Quotes',
        outcome: 'Success',
      },
    ];
  }
  return [
    {
      id: 'LOG-M1',
      dateTime: stamp(5, 13, 12),
      event: 'Message sent',
      details: `Message sent in conversation ${ref}`,
      relatedRecord: ref,
      performedBy: 'Customer',
      source: 'Messages',
      outcome: 'Success',
    },
    {
      id: 'LOG-M2',
      dateTime: stamp(5, 13, 45),
      event: 'Business replied',
      details: 'Business replied to customer message',
      relatedRecord: ref,
      performedBy: 'Business',
      source: 'Messages',
      outcome: 'Success',
    },
    {
      id: 'LOG-M3',
      dateTime: stamp(3, 9, 15),
      event: 'Admin viewed',
      details: `Admin viewed conversation thread ${ref}`,
      relatedRecord: ref,
      performedBy: 'Admin',
      source: 'Messages',
      outcome: 'Success',
    },
  ];
};

/** Status Details — why messaging / Quote Threads are unavailable (or — when Active). */
function getMessageStatusDetails(thread, status) {
  if (thread?.statusDetails) return thread.statusDetails;
  if (status === 'Under Review') {
    const sev = String(thread?.caseSeverity || '').trim();
    if (sev === 'Medium' || sev === 'High') return `Active ${sev} Moderation case`;
    return 'Active Moderation case';
  }
  if (status === 'Blocked') return 'Customer or Business Block applies';
  if (status === 'Restricted') return 'Account restriction prevents messaging';
  return '—';
}

function getQuoteStatusDetails(thread, status) {
  if (thread?.statusDetails) return thread.statusDetails;
  if (status === 'Under Review') {
    const sev = String(thread?.caseSeverity || '').trim();
    if (sev === 'Medium' || sev === 'High') return `Active ${sev} Moderation case`;
    return 'Active Moderation case';
  }
  if (status === 'Blocked') return 'Customer or Business Block applies';
  if (status === 'Restricted') return 'Account restriction prevents Quote Threads';
  return '—';
}

/** Handling Status from Moderation: New · Active · Decided (never parent Under Review). */
function normalizeMessageFlagHandlingStatus(raw) {
  const v = String(raw || '').trim();
  const lower = v.toLowerCase();
  if (['decided', 'resolved', 'cleared', 'approved', 'closed', 'escalated'].includes(lower)) {
    return 'Decided';
  }
  if (['active', 'under review', 'in review', 'attached'].includes(lower)) return 'Active';
  if (['new', 'open', 'pending'].includes(lower)) return 'New';
  if (v === 'New' || v === 'Active' || v === 'Decided') return v;
  return 'New';
}

function getOverviewTotalFlags(thread) {
  if (typeof thread?.totalFlags === 'number') return thread.totalFlags;
  if (typeof thread?.flagsCount === 'number') return thread.flagsCount;
  if (Array.isArray(thread?.flags)) return thread.flags.length;
  if (thread?.flagStatus && thread.flagStatus !== 'None') return 1;
  return 0;
}

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Overview date/time: "30 Jan 2026 · 10:00 PM"
const formatDateTimeOverview = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  const datePart = date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${datePart} · ${timePart}`;
};

const generateMessageDetail = (threadId) => {
  const now = new Date();
  const createdDate = new Date(now.getFullYear(), now.getMonth() - 1, 15);
  const lastMessageDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 2);
  
  return {
    threadId: threadId || 'THR-0001',
    threadType: 'Message',
    source: 'Post: Get in touch',
    initiatedBy: 'Customer',
    status: 'Active',
    region: 'VIC',
    tradeCategory: 'Plumbing',
    customer: {
      id: 'C1234',
      name: 'Jane Miller',
      email: 'jane.miller@example.com',
      avatar: 'https://i.pravatar.cc/150?img=1',
      location: { suburb: 'Clayton', postcode: '3168' },
      profileUrl: '/admin/users/customers/C1234'
    },
    business: {
      id: 'B2044',
      name: 'Northside Renovations',
      avatar: 'https://i.pravatar.cc/150?img=2',
      mainTrade: 'Plumbing',
      region: 'VIC',
      profileUrl: '/admin/users/businesses/B2044'
    },
    type: 'Lead quote',
    flagged: false,
    startedAt: createdDate.toISOString(),
    lastActivity: lastMessageDate.toISOString(),
    totalMessages: 18,
    customerMessageCount: 9,
    businessMessageCount: 9,
    unreadCount: 2,
    attachmentCount: 8,
    messages: [
      {
        id: 'MSG-1',
        sender: 'Customer',
        senderId: 'C1234',
        timestamp: lastMessageDate.toISOString(),
        content: 'Hi, I\'m interested in getting a quote for a bathroom renovation. Can you provide more details?',
        read: true
      },
      {
        id: 'MSG-2',
        sender: 'Business',
        senderId: 'B2044',
        timestamp: new Date(lastMessageDate.getTime() - 3600000).toISOString(),
        content: 'Hello! I\'d be happy to help with your bathroom renovation. What specific work are you looking to have done?',
        read: true
      }
    ],
    threadActivity: [
      {
        id: 'act-1',
        time: '23 Oct 2025 14:22',
        from: { type: 'Customer', id: 'C1234', name: 'Jane Miller', avatar: 'https://i.pravatar.cc/150?img=1' },
        direction: 'Customer → Business',
        type: 'System Event',
        content: 'Thread created',
        attachments: 0,
        threadStatus: 'Open'
      },
      {
        id: 'act-2',
        time: '23 Oct 2025 14:25',
        from: { type: 'Customer', id: 'C1234', name: 'Jane Miller', avatar: 'https://i.pravatar.cc/150?img=1' },
        direction: 'Customer → Business',
        type: 'Message',
        content: 'Hi, I\'m interested in getting a quote for a bathroom renovation. Can you provide more details?',
        attachments: 0,
        threadStatus: null
      },
      {
        id: 'act-3',
        time: '23 Oct 2025 15:30',
        from: { type: 'Business', id: 'B2044', name: 'Northside Renovations', avatar: 'https://i.pravatar.cc/150?img=2' },
        direction: 'Business → Customer',
        type: 'Message',
        content: 'Hello! I\'d be happy to help with your bathroom renovation. What specific work are you looking to have done?',
        attachments: 2,
        threadStatus: null
      },
      {
        id: 'act-4',
        time: '24 Oct 2025 09:15',
        from: { type: 'Customer', id: 'C1234', name: 'Jane Miller', avatar: 'https://i.pravatar.cc/150?img=1' },
        direction: 'Customer → Business',
        type: 'Message',
        content: 'I need tile replacement, new fixtures, and plumbing work. Can you send me some examples of your previous work?',
        attachments: 1,
        threadStatus: null
      },
      {
        id: 'act-5',
        time: '24 Oct 2025 10:45',
        from: { type: 'Business', id: 'B2044', name: 'Northside Renovations', avatar: 'https://i.pravatar.cc/150?img=2' },
        direction: 'Business → Customer',
        type: 'Message',
        content: 'Absolutely! Here are some photos of our recent bathroom renovations. I can provide a detailed quote once I understand your specific requirements.',
        attachments: 5,
        threadStatus: null
      },
      {
        id: 'act-6',
        time: '25 Oct 2025 11:20',
        from: { type: 'Customer', id: 'C1234', name: 'Jane Miller', avatar: 'https://i.pravatar.cc/150?img=1' },
        direction: 'Customer → Business',
        type: 'Message',
        content: 'Thank you! The photos look great. When can you come for a site visit?',
        attachments: 0,
        threadStatus: null
      },
      {
        id: 'act-6b',
        time: '25 Oct 2025 11:45',
        from: { type: 'Business', id: 'B2044', name: 'Northside Renovations', avatar: 'https://i.pravatar.cc/150?img=2' },
        direction: 'Business → Customer',
        type: 'Message',
        content: 'I can do a walk-through next Tuesday or Wednesday afternoon. Which works better for you?',
        attachments: 0,
        threadStatus: null
      },
      {
        id: 'act-6c',
        time: '25 Oct 2025 12:05',
        from: { type: 'Customer', id: 'C1234', name: 'Jane Miller', avatar: 'https://i.pravatar.cc/150?img=1' },
        direction: 'Customer → Business',
        type: 'Message',
        content: 'Tuesday after 3 pm would be perfect.',
        attachments: 0,
        threadStatus: null
      },
      {
        id: 'act-6d',
        time: '25 Oct 2025 12:18',
        from: { type: 'Business', id: 'B2044', name: 'Northside Renovations', avatar: 'https://i.pravatar.cc/150?img=2' },
        direction: 'Business → Customer',
        type: 'Message',
        content: 'Locked in for Tuesday 3:30 pm. I will send a calendar invite with the address confirmation.',
        attachments: 0,
        threadStatus: null
      },
      {
        id: 'act-6e',
        time: '25 Oct 2025 14:00',
        from: { type: 'Customer', id: 'C1234', name: 'Jane Miller', avatar: 'https://i.pravatar.cc/150?img=1' },
        direction: 'Customer → Business',
        type: 'Message',
        content: 'Received, thanks. Side question — do you handle waterproofing behind the new tiles?',
        attachments: 0,
        threadStatus: null
      },
      {
        id: 'act-6f',
        time: '25 Oct 2025 14:22',
        from: { type: 'Business', id: 'B2044', name: 'Northside Renovations', avatar: 'https://i.pravatar.cc/150?img=2' },
        direction: 'Business → Customer',
        type: 'Message',
        content: 'Yes. We use certified waterproof membranes and align with AS 3740 for wet areas. It is included in the quote notes.',
        attachments: 0,
        threadStatus: null
      },
      {
        id: 'act-6g',
        time: '25 Oct 2025 15:40',
        from: { type: 'Customer', id: 'C1234', name: 'Jane Miller', avatar: 'https://i.pravatar.cc/150?img=1' },
        direction: 'Customer → Business',
        type: 'Message',
        content: 'Perfect. Building access is via the rear lane — code is 1847# at the gate.',
        attachments: 0,
        threadStatus: null
      },
      {
        id: 'act-6h',
        time: '25 Oct 2025 16:05',
        from: { type: 'Business', id: 'B2044', name: 'Northside Renovations', avatar: 'https://i.pravatar.cc/150?img=2' },
        direction: 'Business → Customer',
        type: 'Message',
        content: 'Noted for Tuesday. I will bring moisture readings for the subfloor and photos from similar renos.',
        attachments: 1,
        threadStatus: null
      },
      {
        id: 'act-6i',
        time: '25 Oct 2025 17:30',
        from: { type: 'Customer', id: 'C1234', name: 'Jane Miller', avatar: 'https://i.pravatar.cc/150?img=1' },
        direction: 'Customer → Business',
        type: 'Message',
        content: 'Sounds good. Parking is street-only; happy for you to use the driveway briefly to unload tools.',
        attachments: 0,
        threadStatus: null
      },
      {
        id: 'act-6j',
        time: '25 Oct 2025 18:10',
        from: { type: 'Business', id: 'B2044', name: 'Northside Renovations', avatar: 'https://i.pravatar.cc/150?img=2' },
        direction: 'Business → Customer',
        type: 'Message',
        content: 'Appreciate that. I will keep the van tight to the kerb and use the driveway only for unload.',
        attachments: 0,
        threadStatus: null
      },
      {
        id: 'act-6k',
        time: '26 Oct 2025 07:15',
        from: { type: 'Customer', id: 'C1234', name: 'Jane Miller', avatar: 'https://i.pravatar.cc/150?img=1' },
        direction: 'Customer → Business',
        type: 'Message',
        content: 'Morning — quick note: the hot water isolator is behind the linen cupboard if you need it during the visit.',
        attachments: 0,
        threadStatus: null
      },
      {
        id: 'act-6l',
        time: '26 Oct 2025 07:28',
        from: { type: 'Business', id: 'B2044', name: 'Northside Renovations', avatar: 'https://i.pravatar.cc/150?img=2' },
        direction: 'Business → Customer',
        type: 'Message',
        content: 'Thanks for the heads-up. I will check pressures and note any valve upgrades in the written quote.',
        attachments: 0,
        threadStatus: null
      },
      {
        id: 'act-6m',
        time: '26 Oct 2025 07:45',
        from: { type: 'Customer', id: 'C1234', name: 'Jane Miller', avatar: 'https://i.pravatar.cc/150?img=1' },
        direction: 'Customer → Business',
        type: 'Message',
        content: 'Great. If you need ceiling photos I can email the strata report — say the word.',
        attachments: 0,
        threadStatus: null
      },
      {
        id: 'act-6n',
        time: '26 Oct 2025 08:02',
        from: { type: 'Business', id: 'B2044', name: 'Northside Renovations', avatar: 'https://i.pravatar.cc/150?img=2' },
        direction: 'Business → Customer',
        type: 'Message',
        content: 'Please send through — helps us confirm fall heights and exhaust routing before we lock scope.',
        attachments: 0,
        threadStatus: null
      },
      {
        id: 'act-7',
        time: '26 Oct 2025 08:30',
        from: { type: 'System', id: 'SYSTEM', name: 'System' },
        direction: 'System',
        type: 'System Event',
        content: 'Thread auto-closed after 30 days idle',
        attachments: 0,
        threadStatus: 'Closed'
      }
    ],
    activities: [
      {
        id: 'act-1',
        timestamp: '23 Oct 2025 14:22',
        action: 'Thread created',
        details: 'Message thread THR-0001 created by customer'
      },
      {
        id: 'act-2',
        timestamp: '23 Oct 2025 14:25',
        action: 'First message sent',
        details: 'Customer sent initial message'
      },
      {
        id: 'act-3',
        timestamp: '23 Oct 2025 15:30',
        action: 'Business replied',
        details: 'Business responded to customer inquiry'
      },
      {
        id: 'act-4',
        timestamp: '24 Oct 2025 09:15',
        action: 'Customer replied',
        details: 'Customer sent follow-up message'
      },
      {
        id: 'act-5',
        timestamp: '24 Oct 2025 10:45',
        action: 'Business replied',
        details: 'Business provided quote details'
      },
      {
        id: 'act-6',
        timestamp: '25 Oct 2025 11:20',
        action: 'Customer replied',
        details: 'Customer requested additional information'
      },
      {
        id: 'act-7',
        timestamp: '26 Oct 2025 08:30',
        action: 'Business replied',
        details: 'Business provided detailed response'
      }
    ],
    engagements: {
      avgCustomerResponseTime: '2h 15m',
      avgBusinessResponseTime: '1h 30m',
      totalMessages: 18,
      customerMessageCount: 9,
      businessMessageCount: 9,
      conversationDuration: '2 days 18h',
      engagementLevel: 'High',
      attachmentCount: 8,
      flagIssueSummary: 'No flags or issues reported.'
    },
    auditLogs: [
      {
        id: 'LOG-1',
        dateTime: '2025-11-17T13:12:00',
        timestamp: '17 Nov 2025, 01:12 pm',
        event: 'Message sent',
        details: 'User — Jane Miller · Message sent in conversation THR-0001',
        relatedRecord: 'THR-0001',
        performedBy: 'User — Jane Miller',
        detailSummary: 'Message sent in conversation THR-0001',
        source: 'Messages',
        outcome: 'Success',
      },
      {
        id: 'LOG-2',
        dateTime: '2025-11-17T13:45:00',
        timestamp: '17 Nov 2025, 01:45 pm',
        event: 'Business replied',
        details: 'Business — Elite Plumbing · Business replied to customer message',
        relatedRecord: 'THR-0001',
        performedBy: 'Business — Elite Plumbing',
        detailSummary: 'Business replied to customer message',
        source: 'Messages',
        outcome: 'Success',
      },
      {
        id: 'LOG-3',
        dateTime: '2025-11-17T14:30:00',
        timestamp: '17 Nov 2025, 02:30 pm',
        event: 'Quote requested',
        details: 'User — Jane Miller · Quote Q-00412 requested',
        relatedRecord: 'Q-00412',
        performedBy: 'User — Jane Miller',
        detailSummary: 'Quote Q-00412 requested',
        source: 'Quotes',
        outcome: 'Success',
      },
      {
        id: 'LOG-4',
        dateTime: '2025-11-18T09:15:00',
        timestamp: '18 Nov 2025, 09:15 am',
        event: 'Admin viewed',
        details: 'Admin — Mark J. · Admin viewed conversation thread THR-0001',
        relatedRecord: 'THR-0001',
        performedBy: 'Admin — Mark J.',
        detailSummary: 'Admin viewed conversation thread THR-0001',
        source: 'Messages',
        outcome: 'Success',
      },
      {
        id: 'LOG-5',
        dateTime: '2025-11-18T10:30:00',
        timestamp: '18 Nov 2025, 10:30 am',
        event: 'Auto-flagged',
        details: 'System · Message #18 auto-flagged for language violation',
        relatedRecord: 'MC-7712',
        performedBy: 'System',
        detailSummary: 'Message #18 auto-flagged for language violation',
        source: 'Moderation',
        outcome: 'Success',
      },
      {
        id: 'LOG-6',
        dateTime: '2025-11-19T15:20:00',
        timestamp: '19 Nov 2025, 03:20 pm',
        event: 'Quote created',
        details: 'Business — Elite Plumbing · Quote Q-00412 created: "Plumbing repair service - $450"',
        relatedRecord: 'Q-00412',
        performedBy: 'Business — Elite Plumbing',
        detailSummary: 'Quote Q-00412 created: "Plumbing repair service - $450"',
        source: 'Quotes',
        outcome: 'Success',
      },
      {
        id: 'LOG-7',
        dateTime: '2025-11-20T11:45:00',
        timestamp: '20 Nov 2025, 11:45 am',
        event: 'Quote accepted',
        details: 'User — Jane Miller · Quote Q-00412 accepted',
        relatedRecord: 'Q-00412',
        performedBy: 'User — Jane Miller',
        detailSummary: 'Quote Q-00412 accepted',
        source: 'Quotes',
        outcome: 'Success',
      },
      {
        id: 'LOG-8',
        dateTime: '2025-11-20T14:00:00',
        timestamp: '20 Nov 2025, 02:00 pm',
        event: 'Moderation case opened',
        details: 'Admin — Alex R. · Moderation case MC-7712 opened for message #18',
        relatedRecord: 'MC-7712',
        performedBy: 'Admin — Alex R.',
        detailSummary: 'Moderation case MC-7712 opened for message #18',
        source: 'Moderation',
        outcome: 'Success',
      },
      {
        id: 'LOG-9',
        dateTime: '2025-11-21T16:30:00',
        timestamp: '21 Nov 2025, 04:30 pm',
        event: 'Conversation closed',
        details: 'System · Conversation automatically closed after quote acceptance',
        relatedRecord: 'THR-0001',
        performedBy: 'System',
        detailSummary: 'Conversation automatically closed after quote acceptance',
        source: 'Messages',
        outcome: 'Success',
      },
      {
        id: 'LOG-10',
        dateTime: '2025-11-22T09:00:00',
        timestamp: '22 Nov 2025, 09:00 am',
        event: 'Archived',
        details: 'Admin — Mark J. · Admin archived completed conversation',
        relatedRecord: 'THR-0001',
        performedBy: 'Admin — Mark J.',
        detailSummary: 'Admin archived completed conversation',
        source: 'Messages',
        outcome: 'Success',
      },
    ],
    notes: [
      {
        id: 'note-1',
        timestamp: '26 Oct 2025, 10:05',
        author: 'Rachel Brown',
        email: 'admin@tradesposter.com',
        category: 'Support',
        content: 'Customer reported issue with message delivery. Verified messages are being delivered correctly. No action needed.',
        outcome: 'Resolved',
        linkedRef: { id: 'THR-0001', type: 'message' }
      }
    ],
    // Quote data
    quoteId: 'Q-00412',
    quoteStatus: 'Open',
    quoteDescription: 'Plumbing repair service: Fix leaking pipe in bathroom, replace faulty faucet, and install new shower head. Includes all materials and labor. Estimated completion time: 2-3 hours.',
    quotePrice: 450.00,
    quoteBreakdown: [
      { item: 'Labor (3 hours)', amount: 180.00 },
      { item: 'Pipe replacement materials', amount: 120.00 },
      { item: 'New faucet', amount: 85.00 },
      { item: 'Shower head', amount: 45.00 },
      { item: 'Additional supplies', amount: 20.00 }
    ],
    quoteCreated: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    quoteUpdated: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    quoteAttachments: 2,
    quoteAttachmentFiles: [
      { name: 'bathroom_photo_1.jpg', size: '2.3 MB', url: '#' },
      { name: 'quote_drawing.pdf', size: '856 KB', url: '#' }
    ],
    groupQuoteId: null,
    groupQuoteInvitedCount: 0,
    groupQuoteDescription: null,
    groupQuoteStatus: null,
    groupQuoteCreated: null,
    // Moderation & Flags data
    moderationCases: [
      {
        caseId: 'MC-7712',
        type: 'Moderation Case',
        status: 'Under Review',
        severity: 'Medium',
        object: 'Message #18',
        messageNumber: 18,
        reason: 'R-MSG-02: Abusive language',
        ruleCode: 'R-MSG-02',
        raisedBy: 'System Auto-Flag',
        createdAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        openedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        lastUpdated: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString()
      }
    ],
    flags: [
      {
        id: 'F-99812',
        flagId: 'F-99812',
        type: 'Auto-Flag',
        status: 'Attached',
        severity: 'Low',
        object: 'Quote Q-00412',
        quoteId: 'Q-00412',
        reason: 'Suspicious pricing',
        ruleCode: 'R-QTE-05',
        raisedBy: 'System',
        createdAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        lastUpdated: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        id: 'F-99813',
        flagId: 'F-99813',
        type: 'User Report',
        status: 'New',
        severity: 'High',
        object: 'Full Conversation',
        reason: 'Inappropriate content',
        ruleCode: 'R-MSG-01',
        raisedBy: 'Customer',
        createdAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        lastUpdated: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString()
      }
    ]
  };
};

const MessageDetail = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() =>
    ['overview', 'timeline', 'quoteThread', 'flags', 'logs', 'audit-log'].includes(
      location.state?.activeTab,
    )
      ? location.state.activeTab === 'messages'
        ? 'timeline'
        : location.state.activeTab === 'audit-log'
          ? 'logs'
          : location.state.activeTab
      : 'overview'
  );
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditLogCurrentPage, setAuditLogCurrentPage] = useState(1);
  const [copiedOverviewField, setCopiedOverviewField] = useState(null);
  const [highlightedMessageThreadId, setHighlightedMessageThreadId] = useState(null);

  // Notes modal state
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [viewingNote, setViewingNote] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [pinnedNotes, setPinnedNotes] = useState(new Set());
  const [noteRowActionsOpen, setNoteRowActionsOpen] = useState({});
  const noteRowActionsRefs = useRef({});
  const [noteForm, setNoteForm] = useState({
    noteText: ''
  });
  
  // Dropdown states for logs filters
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [actorOpen, setActorOpen] = useState(false);
  const [moduleOpen, setModuleOpen] = useState(false);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  
  // Filter values
  const [filters, setFilters] = useState({
    dateRange: 'All',
    actor: 'All',
    module: 'All',
    outcome: 'All'
  });
  
  // Refs for click outside
  const dateRangeRef = useRef(null);
  const actorRef = useRef(null);
  const moduleRef = useRef(null);
  const outcomeRef = useRef(null);
  
  const [flagsCurrentPage, setFlagsCurrentPage] = useState(1);

  // Flags tab filters
  const [flagsFilters, setFlagsFilters] = useState({
    status: 'All',
    reason: 'All',
    source: 'All',
    openedDateFrom: null,
    openedDateTo: null
  });
  
  // Flags filter dropdown states
  const [flagsStatusFilterOpen, setFlagsStatusFilterOpen] = useState(false);
  const [flagsReasonFilterOpen, setFlagsReasonFilterOpen] = useState(false);
  const [flagsSourceFilterOpen, setFlagsSourceFilterOpen] = useState(false);
  const [flagsDateFilterOpen, setFlagsDateFilterOpen] = useState(false);
  
  // Refs for flags filters
  const flagsStatusFilterRef = useRef(null);
  const flagsReasonFilterRef = useRef(null);
  const flagsSourceFilterRef = useRef(null);
  const flagsDateFilterRef = useRef(null);
  
  // Flags row actions state
  const [flagsRowActionsOpen, setFlagsRowActionsOpen] = useState({});
  const flagsRowActionsRefs = useRef({});
  
  // Quotes tab filters
  const [quotesFilters, setQuotesFilters] = useState({
    type: 'All',
    status: 'All',
    issuedDateFrom: null,
    issuedDateTo: null
  });
  
  // Quotes filter dropdown states
  const [quotesTypeFilterOpen, setQuotesTypeFilterOpen] = useState(false);
  const [quotesStatusFilterOpen, setQuotesStatusFilterOpen] = useState(false);
  const [quotesDateFilterOpen, setQuotesDateFilterOpen] = useState(false);
  
  // Refs for quotes filters
  const quotesTypeFilterRef = useRef(null);
  const quotesStatusFilterRef = useRef(null);
  const quotesDateFilterRef = useRef(null);
  
  // Quotes row actions state
  const [quotesRowActionsOpen, setQuotesRowActionsOpen] = useState({});
  const quotesRowActionsRefs = useRef({});
  
  // Moderation row actions state
  const [moderationRowActionsOpen, setModerationRowActionsOpen] = useState({});
  const moderationRowActionsRefs = useRef({});
  
  // Click outside handlers
  useClickOutside(dateRangeRef, () => setDateRangeOpen(false));
  useClickOutside(actorRef, () => setActorOpen(false));
  useClickOutside(moduleRef, () => setModuleOpen(false));
  useClickOutside(outcomeRef, () => setOutcomeOpen(false));
  useClickOutside(flagsStatusFilterRef, () => setFlagsStatusFilterOpen(false));
  useClickOutside(flagsReasonFilterRef, () => setFlagsReasonFilterOpen(false));
  useClickOutside(flagsSourceFilterRef, () => setFlagsSourceFilterOpen(false));
  useClickOutside(flagsDateFilterRef, () => setFlagsDateFilterOpen(false));
  useClickOutside(quotesTypeFilterRef, () => setQuotesTypeFilterOpen(false));
  useClickOutside(quotesStatusFilterRef, () => setQuotesStatusFilterOpen(false));
  useClickOutside(quotesDateFilterRef, () => setQuotesDateFilterOpen(false));
  
  // Toggle moderation row actions
  const toggleModerationRowActions = (itemId) => {
    setModerationRowActionsOpen((prev) => {
      if (prev[itemId]) return {};
      return { [itemId]: true };
    });
  };
  
  // Close moderation row actions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(moderationRowActionsRefs.current).forEach(itemId => {
        const ref = moderationRowActionsRefs.current[itemId];
        if (ref && !ref.contains(event.target)) {
          setModerationRowActionsOpen(prev => ({
            ...prev,
            [itemId]: false
          }));
        }
      });
    };
    
    if (Object.keys(moderationRowActionsOpen).some(key => moderationRowActionsOpen[key])) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [moderationRowActionsOpen]);

  // Close note row actions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(noteRowActionsRefs.current).forEach(noteId => {
        const ref = noteRowActionsRefs.current[noteId];
        if (ref && !ref.contains(event.target)) {
          setNoteRowActionsOpen(prev => ({
            ...prev,
            [noteId]: false
          }));
        }
      });
    };
    
    if (Object.keys(noteRowActionsOpen).some(key => noteRowActionsOpen[key])) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [noteRowActionsOpen]);

  // Close quotes row actions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(quotesRowActionsRefs.current).forEach(quoteId => {
        const ref = quotesRowActionsRefs.current[quoteId];
        if (ref && !ref.contains(event.target)) {
          setQuotesRowActionsOpen(prev => ({
            ...prev,
            [quoteId]: false
          }));
        }
      });
    };
    
    if (Object.keys(quotesRowActionsOpen).some(key => quotesRowActionsOpen[key])) {
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [quotesRowActionsOpen]);

  // Filter change handler
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const threadRef = useMemo(() => resolveThreadRef(thread), [thread]);
  const isQuoteThread = useMemo(
    () => isQuoteThreadType(thread?.type || thread?.threadType),
    [thread?.type, thread?.threadType],
  );

  // Audit Log rows — Screen 2 Messages / Quotes
  const filteredAuditLogs = useMemo(() => {
    if (!thread) return [];
    const ref = threadRef || '—';
    let rows = (thread.auditLogs || [])
      .map((log) => normalizeThreadAuditLog(log, ref))
      .filter((log) => log && !isEscalatedAuditLogEntry(log));

    if (filters.dateRange !== 'All') {
      let daysAgo = 0;
      if (filters.dateRange === 'Last 7 days') daysAgo = 7;
      else if (filters.dateRange === 'Last 14 days') daysAgo = 14;
      else if (filters.dateRange === 'Last 30 days') daysAgo = 30;
      else if (filters.dateRange === 'Last 90 days') daysAgo = 90;
      if (daysAgo > 0) {
        const cutoffDate = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);
        rows = rows.filter((log) => {
          const logDate = new Date(log.dateTime || log.timestamp);
          return !Number.isNaN(logDate.getTime()) && logDate >= cutoffDate;
        });
      }
    }

    if (filters.actor !== 'All') {
      rows = rows.filter((log) => {
        const performedBy = log.performedBy || log.details || '';
        if (filters.actor === 'Customer') {
          return performedBy.includes('User') || performedBy.includes('Customer');
        }
        if (filters.actor === 'Business') return performedBy.includes('Business');
        if (filters.actor === 'Admin') return performedBy.includes('Admin');
        if (filters.actor === 'Moderator') return performedBy.includes('Moderator');
        if (filters.actor === 'System') return performedBy.includes('System');
        return true;
      });
    }

    if (filters.module !== 'All') {
      rows = rows.filter((log) => log.source === filters.module);
    }

    if (filters.outcome !== 'All') {
      rows = rows.filter((log) => {
        let outcome = log.outcome || 'Success';
        if (log.event?.includes('Blocked') || log.details?.includes('Blocked')) outcome = 'Blocked';
        else if (log.event?.includes('Failed') || log.details?.includes('Failed')) outcome = 'Failed';
        return outcome === filters.outcome;
      });
    }

    return [...rows].sort((a, b) => {
      const dateA = new Date(a.dateTime || a.timestamp || 0).getTime();
      const dateB = new Date(b.dateTime || b.timestamp || 0).getTime();
      return dateB - dateA;
    });
  }, [thread, threadRef, filters]);

  const auditPagination = useMemo(
    () => getDetailTablePagination(filteredAuditLogs.length, auditLogCurrentPage),
    [filteredAuditLogs.length, auditLogCurrentPage],
  );
  const paginatedAuditRows = useMemo(
    () => auditPagination.paginate(filteredAuditLogs),
    [auditPagination, filteredAuditLogs],
  );

  // Ref for messages container to scroll to bottom
  const messagesContainerRef = useRef(null);

  // Notes handlers
  const openNotesModal = (note = null) => {
    if (note) {
      setEditingNote(note);
      setNoteForm({
        noteText: note.noteContent || note.content || ''
      });
    } else {
      setEditingNote(null);
      setNoteForm({
        noteText: ''
      });
    }
    setNotesModalOpen(true);
  };

  const closeNotesModal = () => {
    setNotesModalOpen(false);
    setEditingNote(null);
    setNoteForm({
      noteText: ''
    });
  };

  const handleNoteSave = () => {
    if (!noteForm.noteText) {
      alert('Note text is required');
      return;
    }

    const now = new Date();
    const timestamp = `${now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    
    if (editingNote) {
      setThread(prev => ({
        ...prev,
        notes: prev.notes.map(note => 
          note.id === editingNote.id 
            ? {
                ...note,
                content: noteForm.noteText,
                noteContent: noteForm.noteText,
                timestamp: timestamp
              }
            : note
        )
      }));
    } else {
      const newNote = {
        id: `note-${Date.now()}`,
        timestamp: timestamp,
        createdAt: now.toISOString(),
        author: 'Rachel Brown',
        adminName: 'Rachel Brown',
        content: noteForm.noteText,
        noteContent: noteForm.noteText
      };
      
      setThread(prev => ({
        ...prev,
        notes: [newNote, ...(prev.notes || [])]
      }));
    }
    
    closeNotesModal();
  };

  const handleViewNote = (note) => {
    setViewingNote(note);
    setNoteRowActionsOpen(prev => ({ ...prev, [note.id]: false }));
  };

  const closeViewNoteModal = () => {
    setViewingNote(null);
  };

  const handleNotePin = (noteId) => {
    setPinnedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
    setNoteRowActionsOpen(prev => ({ ...prev, [noteId]: false }));
  };

  const toggleNoteRowActions = (noteId) => {
    setNoteRowActionsOpen((prev) => {
      if (prev[noteId]) return {};
      return { [noteId]: true };
    });
  };

  const getRefLink = (ref) => {
    if (!ref || !ref.id) return null;
    if (ref.type === 'message') return `/admin/communication/all/${ref.id}`;
    if (ref.type === 'customer') return `/admin/users/customers/${ref.id}`;
    if (ref.type === 'business') return `/admin/users/businesses/${ref.id}`;
    return null;
  };

  const getRefDisplay = (ref) => {
    if (!ref || !ref.id) return '—';
    return ref.id;
  };

  useEffect(() => {
    const fetchThreadDetail = async () => {
      setLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 300));
        const passedMessage = location.state?.message;
        const scenario = getMessageScenarioByConversationId(threadId);

        if (scenario) {
          const s = scenario;
          const conv = s.conversation;
          const c = s.case;
          const f = s.flag;
          const firstMsg = s.messages[0];
          const lastMsg = s.messages[s.messages.length - 1];
          const status = c?.status || null;
          const threadActivity = [
            {
              id: 'act-0',
              time: formatDateTime(conv.startedAt),
              from: { type: firstMsg?.sender === 'Customer' ? 'Customer' : 'Business', id: firstMsg?.senderId, name: firstMsg?.sender === 'Customer' ? s.customer.name : s.business.businessName },
              direction: firstMsg?.direction || (firstMsg?.sender === 'Customer' ? 'Customer → Business' : 'Business → Customer'),
              type: 'System Event',
              content: 'Thread created',
              attachments: 0,
              threadStatus: 'Open',
            },
            ...s.messages.map((m, i) => ({
              id: m.id || `act-${i + 1}`,
              time: formatDateTime(m.timestamp),
              from: { type: m.sender, id: m.senderId, name: m.sender === 'Customer' ? s.customer.name : s.business.businessName },
              direction: m.direction || (m.sender === 'Customer' ? 'Customer → Business' : 'Business → Customer'),
              type: 'Message',
              content: m.text,
              attachments: 0,
              threadStatus: null,
              messageStatus: m.messageStatus || 'Sent',
              isFlagged: m.isFlagged,
              flagId: f?.messageThreadId === m.id ? f.flagId : null,
            })),
          ];
          const linkedCaseId = f?.linkedCaseId || c?.caseId || null;
          const flags = f
            ? [{
                flagId: f.flagId,
                source: f.source,
                flaggedBy: f.flaggedBy,
                messageThreadId: f.messageThreadId,
                category: f.category,
                trigger: f.trigger,
                reason: f.reason || f.trigger,
                severity: f.severity,
                status: f.status,
                handlingStatus: f.status || c?.status || null,
                linkedCaseId,
                linkedCaseType: linkedCaseId ? 'Moderation' : null,
                createdAt: f.createdAt,
                updatedAt: f.updatedAt,
              }]
            : [];
          const threadActivityWithFlags = threadActivity.map((activity) => {
            if (activity.type !== 'Message') return activity;
            const linkedFlag = flags.find((flag) => flag.messageThreadId === activity.id);
            return {
              ...activity,
              isFlagged: Boolean(activity.isFlagged || linkedFlag),
              flagId: linkedFlag?.flagId || activity.flagId || null,
            };
          });
          const moderationCases = c
            ? [{
                caseId: c.caseId,
                id: c.caseId,
                type: 'Moderation',
                status,
                severity: c.severity,
                assignedTo: c.assignedTo,
                created: c.created,
                updated: c.updated,
              }]
            : [];
          const recordStatus = deriveMessageThreadStatus({
            status: undefined,
            messagingState: s.messagingState,
            caseStatus: status,
            caseSeverity: c?.severity,
            underReview: false,
          });
          const startedFromType = conv.startedFrom === 'Post' ? 'Post' : 'Business Profile';
          const built = {
            threadId: conv.conversationId,
            conversationId: conv.conversationId,
            messageId: conv.conversationId,
            threadType: 'Message',
            type: 'Message',
            source: startedFromType === 'Post'
              ? `Post: ${conv.startingPostTitle || 'Post'}`
              : 'Business Profile',
            startedFromType,
            startingPostTitle: conv.startingPostTitle || null,
            startingPostId: conv.startingPostId || null,
            initiatedBy: firstMsg?.sender === 'Customer' ? 'Customer' : 'Business',
            status: recordStatus,
            messagingState: s.messagingState || 'Active',
            region: 'VIC',
            tradeCategory: s.business.category || 'Plumbing',
            customer: {
              id: s.customer.customerId,
              customerId: s.customer.customerId,
              name: s.customer.name,
              avatar: 'https://i.pravatar.cc/150?img=32',
              profileUrl: `/admin/users/customers/${s.customer.customerId}`,
              status: s.customer.accountStatus,
              accountStatus: s.customer.accountStatus,
              memberSince: s.customer.memberSince || null,
              lastActive: s.customer.lastActive || null,
              blockStatus: s.customer.blockStatus || null,
            },
            business: {
              id: s.business.businessId,
              businessId: s.business.businessId,
              name: s.business.businessName,
              avatar: 'https://i.pravatar.cc/150?img=65',
              mainTrade: s.business.category,
              category: s.business.category,
              region: 'VIC',
              profileUrl: `/admin/users/businesses/${s.business.businessId}`,
              status: s.business.accountStatus,
              accountStatus: s.business.accountStatus,
              verificationStatus: s.business.verificationStatus || null,
              memberSince: s.business.memberSince || null,
              blockStatus: s.business.blockStatus || null,
            },
            startedAt: conv.startedAt,
            lastActivity: lastMsg?.timestamp || conv.startedAt,
            lastMessageOn: lastMsg?.timestamp || conv.startedAt,
            lastUpdated: conv.lastUpdated || lastMsg?.timestamp || conv.startedAt,
            totalMessages: s.messages.length,
            totalMessageThreads: s.messages.length,
            totalFlags: flags.length,
            flagStatus: flags.length ? 'Has Flags' : 'None',
            flagged: flags.length > 0,
            underReview: recordStatus === 'Under Review',
            quoteStatus: 'No Quote',
            quoteId: null,
            groupQuoteId: null,
            threadActivity: threadActivityWithFlags,
            flags,
            moderationCases,
            caseStatus: status,
            caseSeverity: c?.severity || null,
            moderationCaseId: c?.caseId || null,
            auditLogs: buildDefaultThreadAuditLogs(
              conv?.conversationId || threadId,
              false,
            ),
          };
          setThread(
            passedMessage
              ? {
                  ...built,
                  ...passedMessage,
                  customer: { ...built.customer, ...passedMessage.customer },
                  business: { ...built.business, ...passedMessage.business },
                  flags,
                  moderationCases,
                  threadActivity: threadActivityWithFlags,
                  auditLogs:
                    passedMessage.auditLogs ||
                    built.auditLogs ||
                    buildDefaultThreadAuditLogs(
                      conv?.conversationId || threadId,
                      false,
                    ),
                }
              : built
          );
          return;
        }

        const quoteScenario = getQuoteScenarioByQuoteId(threadId);
        if (quoteScenario) {
          const s = quoteScenario;
          const q = s.quote;
          const c = s.case;
          const f = s.flag;
          const firstMsg = s.chatMessages[0];
          const lastMsg = s.chatMessages[s.chatMessages.length - 1];
          const status = c
            ? (c.status === 'In Review' ? 'Under Review' : (c.status || 'Under Review'))
            : null;
          const threadActivity = [
            {
              id: 'act-0',
              time: formatDateTime(q.submittedAt),
              from: { type: firstMsg?.sender === 'Customer' ? 'Customer' : 'Business', id: firstMsg?.senderId, name: firstMsg?.sender === 'Customer' ? s.customer.name : s.business.businessName },
              direction: firstMsg?.direction || (firstMsg?.sender === 'Customer' ? 'Customer → Business' : 'Business → Customer'),
              type: 'System Event',
              content: 'Quote submitted',
              attachments: 0,
              threadStatus: 'Open',
            },
            ...s.chatMessages.map((m, i) => ({
              id: m.id || `act-${i + 1}`,
              time: formatDateTime(m.timestamp),
              from: { type: m.sender, id: m.senderId, name: m.sender === 'Customer' ? s.customer.name : s.business.businessName },
              direction: m.direction || (m.sender === 'Customer' ? 'Customer → Business' : 'Business → Customer'),
              type: 'Message',
              content: m.text,
              attachments: 0,
              threadStatus: null,
              messageStatus: m.messageStatus || 'Sent',
              isFlagged: m.isFlagged,
              flagId: f?.quoteThreadId === m.id ? f.flagId : null,
            })),
          ];
          const flags = f
            ? [{
                flagId: f.flagId,
                source: f.source,
                flaggedBy: f.flaggedBy,
                messageThreadId: f.quoteThreadId,
                quoteThreadId: f.quoteThreadId,
                category: f.category,
                trigger: f.trigger,
                reason: f.reason || f.trigger,
                severity: f.severity,
                status: f.status === 'Under Review' ? 'Under Review' : f.status,
                linkedCaseId: f.linkedCaseId || c?.caseId || null,
                linkedCaseType: (f.linkedCaseId || c?.caseId) ? 'Moderation' : null,
                createdAt: f.createdAt,
                updatedAt: f.updatedAt,
              }]
            : [];
          const moderationCases = c
            ? [{
                caseId: c.caseId,
                id: c.caseId,
                type: 'Moderation',
                status,
                severity: c.severity,
                assignedTo: c.assignedTo,
                created: c.created,
                updated: c.updated,
              }]
            : [];
          const recordStatus = normalizeQuoteStatus(undefined, s.messagingState, {
            messagingState: s.messagingState,
            caseStatus: status,
            caseSeverity: c?.severity,
            underReview: status === 'Under Review',
          });
          const attachmentCount = q.containsMedia ? (q.attachmentsSummary ? 2 : 1) : 0;
          const built = {
            threadId: q.quoteId,
            conversationId: q.quoteId,
            threadType: 'Quote',
            type: 'Quote',
            source: q.requestSource || `Quote: ${q.title || 'Quote request'}`,
            requestSource: getQuoteRequestSourceDisplay(q.requestSource),
            quoteRequestId: q.quoteRequestId || null,
            recipientMatchId: q.recipientMatchId || null,
            initiatedBy: firstMsg?.sender === 'Customer' ? 'Customer' : 'Business',
            status: recordStatus,
            messagingState: s.messagingState || 'Active',
            region: 'VIC',
            tradeCategory: s.business.category || 'Plumbing',
            customer: {
              id: s.customer.customerId,
              customerId: s.customer.customerId,
              name: s.customer.name,
              avatar: s.customer.avatar || 'https://i.pravatar.cc/150?img=32',
              profileUrl: `/admin/users/customers/${s.customer.customerId}`,
              status: s.customer.accountStatus,
              accountStatus: s.customer.accountStatus,
              memberSince: s.customer.memberSince || null,
              lastActive: s.customer.lastActive || null,
              blockStatus: s.customer.blockStatus || null,
            },
            business: {
              id: s.business.businessId,
              businessId: s.business.businessId,
              name: s.business.businessName,
              avatar: s.business.avatar || 'https://i.pravatar.cc/150?img=65',
              mainTrade: s.business.category,
              category: s.business.category,
              region: 'VIC',
              profileUrl: `/admin/users/businesses/${s.business.businessId}`,
              status: s.business.accountStatus,
              accountStatus: s.business.accountStatus,
              verificationStatus: s.business.verificationStatus || null,
              memberSince: s.business.memberSince || null,
              blockStatus: s.business.blockStatus || null,
            },
            startedAt: q.submittedAt,
            submittedOn: q.submittedAt,
            deliveredOn: q.deliveredAt || q.submittedAt,
            lastActivity: lastMsg?.timestamp || q.submittedAt,
            lastQuoteThreadOn: lastMsg?.timestamp || q.submittedAt,
            lastUpdated: q.lastUpdated || lastMsg?.timestamp || q.submittedAt,
            totalMessages: s.chatMessages.length,
            totalQuoteThreads: s.chatMessages.length,
            totalFlags: flags.length,
            quoteRequestStatus: q.quoteRequestStatus || 'Distributed',
            responseStatus: q.responseStatus || 'Responded',
            quoteRequestSnapshot: q.snapshot || null,
            distributionReference: q.distribution || null,
            flagStatus: flags.some((flag) => flag.status === 'Under Review' || flag.status === 'Open')
              ? 'Under Review'
              : 'None',
            flagged: flags.length > 0,
            underReview: recordStatus === 'Under Review',
            quoteStatus: recordStatus,
            quoteId: q.quoteId,
            quoteTitle: q.title,
            quoteDescription: q.description,
            quoteLocation: q.location || '—',
            quoteAttachmentsSummary: q.attachmentsSummary || '',
            attachmentCount,
            serviceRequested: q.serviceRequested || s.business.category || '—',
            finalOutcome: q.finalOutcome ?? null,
            groupQuoteId: null,
            threadActivity,
            flags,
            moderationCases,
            caseStatus: status,
            caseSeverity: c?.severity || null,
            moderationCaseId: c?.caseId || null,
            auditLogs: buildDefaultThreadAuditLogs(q.quoteId || threadId, true),
          };
          setThread(
            passedMessage
              ? {
                  ...built,
                  ...passedMessage,
                  customer: { ...built.customer, ...passedMessage.customer },
                  business: { ...built.business, ...passedMessage.business },
                  flags,
                  moderationCases,
                  threadActivity,
                  auditLogs:
                    passedMessage.auditLogs ||
                    built.auditLogs ||
                    buildDefaultThreadAuditLogs(q.quoteId || threadId, true),
                }
              : built
          );
          return;
        }

        if (passedMessage) {
          const threadData = generateMessageDetail(threadId);
          const mergedThread = {
            ...threadData,
            threadId: passedMessage.threadId || passedMessage.id || threadId,
            status: passedMessage.status || threadData.status,
            type: passedMessage.type || threadData.threadType,
            customer: {
              ...threadData.customer,
              id: passedMessage.customer?.id || threadData.customer.id,
              name: passedMessage.customer?.name || threadData.customer.name,
              avatar: passedMessage.customer?.avatar || threadData.customer.avatar,
              location: passedMessage.customer?.location || threadData.customer.location,
              profileUrl: `/admin/users/customers/${passedMessage.customer?.id || threadData.customer.id}`
            },
            business: {
              ...threadData.business,
              id: passedMessage.business?.id || threadData.business.id,
              name: passedMessage.business?.name || threadData.business.name,
              avatar: passedMessage.business?.avatar || threadData.business.avatar,
              mainTrade: passedMessage.tradeCategory || threadData.business.mainTrade,
              region: passedMessage.region || threadData.business.region,
              profileUrl: `/admin/users/businesses/${passedMessage.business?.id || threadData.business.id}`
            },
            initiatedBy: passedMessage.conversationStartedBy || threadData.initiatedBy,
            startedAt: passedMessage.created || threadData.startedAt,
            lastActivity: passedMessage.lastActivity || threadData.lastActivity,
            totalMessages: passedMessage.messageCount || threadData.totalMessages,
            flagStatus: passedMessage.flagStatus || (threadData.flagged ? 'Flagged' : 'None'),
            quoteStatus: passedMessage.quoteStatus || threadData.quoteStatus || 'No Quote',
            quoteId: passedMessage.quoteId || threadData.quoteId || null,
            groupQuoteId: passedMessage.groupQuoteId || threadData.groupQuoteId || null,
            quoteDescription: passedMessage.quoteDescription || threadData.quoteDescription || null,
            quotePrice: passedMessage.quotePrice || threadData.quotePrice || null,
            quoteBreakdown: passedMessage.quoteBreakdown || threadData.quoteBreakdown || null,
            quoteCreated: passedMessage.quoteCreated || threadData.quoteCreated || null,
            quoteUpdated: passedMessage.quoteUpdated || threadData.quoteUpdated || null,
            quoteAttachments: passedMessage.quoteAttachments || threadData.quoteAttachments || 0,
            quoteAttachmentFiles: passedMessage.quoteAttachmentFiles || threadData.quoteAttachmentFiles || [],
            groupQuoteInvitedCount: passedMessage.groupQuoteInvitedCount || threadData.groupQuoteInvitedCount || 0,
            groupQuoteDescription: passedMessage.groupQuoteDescription || threadData.groupQuoteDescription || null,
            groupQuoteStatus: passedMessage.groupQuoteStatus || threadData.groupQuoteStatus || null,
            groupQuoteCreated: passedMessage.groupQuoteCreated || threadData.groupQuoteCreated || null,
            moderationCases: passedMessage.moderationCases || threadData.moderationCases || [],
            flags: passedMessage.flags || threadData.flags || []
          };
          setThread(mergedThread);
        } else {
          setThread(generateMessageDetail(threadId));
        }
      } catch (err) {
        console.error('Error fetching thread detail:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchThreadDetail();
  }, [threadId, location.state]);

  useEffect(() => {
    setFlagsCurrentPage(1);
    setAuditLogCurrentPage(1);
  }, [threadId]);

  useEffect(() => {
    if (!thread?.flags?.length) return;
    const pages = Math.max(1, Math.ceil(thread.flags.length / CONFIG_LIST_PAGE_SIZE));
    setFlagsCurrentPage((p) => Math.min(p, pages));
  }, [thread?.flags?.length]);

  useEffect(() => {
    if (!thread) return;
    const ref = resolveThreadRef(thread);
    if (!ref) return;
    const quote = isQuoteThreadType(thread.type || thread.threadType);
    const synced = refreshThreadAuditFromStore(ref, quote, thread.auditLogs || []);
    const sameLength = (thread.auditLogs || []).length === synced.length;
    const alreadyNormalized =
      sameLength &&
      (thread.auditLogs || []).every(
        (log, index) => log?.event === synced[index]?.event && log?.details === synced[index]?.details,
      );
    if (!alreadyNormalized) {
      setThread((prev) => (prev ? { ...prev, auditLogs: synced } : prev));
    }
  }, [thread?.threadId, thread?.quoteId, thread?.messageId, thread?.type, thread?.threadType]);

  // Open a specific tab when navigated with state (e.g. from moderation queue → quote detail flags tab)
  useEffect(() => {
    const tabFromState = location.state?.activeTab;
    if (
      tabFromState &&
      ['overview', 'timeline', 'quoteThread', 'flags', 'logs', 'audit-log'].includes(tabFromState)
    ) {
      if (tabFromState === 'messages') {
        const type = thread?.type || thread?.threadType || '';
        setActiveTab(type.toLowerCase().includes('quote') ? 'quoteThread' : 'timeline');
      } else if (tabFromState === 'audit-log') {
        setActiveTab('logs');
      } else {
        setActiveTab(tabFromState);
      }
    }
  }, [location.state?.activeTab, thread?.type, thread?.threadType]);

  // Deep-link tab (e.g. /admin/quotes/all/:id?tab=responses → Quote Activity)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'responses' || tabParam === 'quoteThread') {
      setActiveTab('quoteThread');
    }
  }, [searchParams]);

  // Switch to overview if timeline/quoteThread tab is active but thread type doesn't match
  useEffect(() => {
    if (thread) {
      const type = thread.type || thread.threadType || '';
      const isQuote = type.toLowerCase().includes('quote');
      
      // Switch to Quote Activity when Messages tab is active on a quote thread
      if (activeTab === 'timeline' && isQuote) {
        setActiveTab('quoteThread');
      }
      // Switch to Messages when Quote Activity is active on a non-quote thread
      if (activeTab === 'quoteThread' && !isQuote) {
        setActiveTab('timeline');
      }
    }
  }, [thread, activeTab]);

  // Scroll messages container to bottom when messages change or page changes
  useEffect(() => {
    if (highlightedMessageThreadId) return undefined;
    if ((activeTab === 'timeline' || activeTab === 'quoteThread') && messagesContainerRef.current) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [thread?.threadActivity, activeTab, highlightedMessageThreadId]);
  
  // Scroll messages container to bottom immediately when timeline tab is first opened
  useEffect(() => {
    if (highlightedMessageThreadId) return undefined;
    if ((activeTab === 'timeline' || activeTab === 'quoteThread') && messagesContainerRef.current) {
      const timer = setTimeout(() => {
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [activeTab, highlightedMessageThreadId]);

  // Scroll to highlighted bubble when Flags → View Thread opens Message Threads / Quote Threads
  useEffect(() => {
    const isThreadTab = activeTab === 'timeline' || activeTab === 'quoteThread';
    if (!isThreadTab || !highlightedMessageThreadId) return undefined;
    const timer = setTimeout(() => {
      const el = document.getElementById(`message-thread-${highlightedMessageThreadId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [activeTab, highlightedMessageThreadId]);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'timeline', label: 'Message Threads' },
    { id: 'quoteThread', label: 'Quote Threads' },
    { id: 'flags', label: 'Flags' },
    { id: 'logs', label: 'Audit Log' },
  ];

  if (loading || !thread) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>Loading thread details…</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  const renderOverview = () => {
    const type = thread.type || thread.threadType || '';
    const isQuote = type.toLowerCase().includes('quote');
    const isMessage = !isQuote;

    const idValue = isQuote
      ? (thread.quoteId || thread.threadId || '—')
      : (thread.messageId || thread.threadId || thread.conversationId || '—');
    const quoteRequestId = thread.quoteRequestId || null;
    const requestSource = isQuote
      ? getQuoteRequestSourceDisplay(thread.requestSource || thread.source)
      : (thread.requestSource || thread.source || '—');

    const displayQuoteStatus = normalizeQuoteStatus(thread.quoteStatus || thread.status, thread.messagingState, {
      underReview: thread.underReview,
      caseStatus: thread.caseStatus,
      caseSeverity: thread.caseSeverity,
      messagingState: thread.messagingState,
    });
    const threadStatus = normalizeMessageStatus(
      thread.status,
      thread.messagingState,
      thread.messages || thread.threadActivity?.filter((a) => a.type === 'Message'),
      {
        underReview: thread.underReview,
        caseStatus: thread.caseStatus,
        caseSeverity: thread.caseSeverity,
      }
    );
    const displayThreadStatus = threadStatus;
    const statusDetails = isQuote
      ? getQuoteStatusDetails(thread, displayQuoteStatus)
      : getMessageStatusDetails(thread, displayThreadStatus);
    const quoteRequestStatus = thread.quoteRequestStatus || '—';
    const responseStatus = thread.responseStatus || '—';

    const customerId = thread.customer?.id || thread.customer?.customerId;
    const businessId = thread.business?.id || thread.business?.businessId;
    const customerName = thread.customer?.name || '—';
    const businessName = thread.business?.name || thread.business?.businessName || '—';
    const customerUrl = customerId ? `/admin/users/customers/${customerId}` : null;
    const businessUrl = businessId ? `/admin/users/businesses/${businessId}` : null;
    const customerAccountStatus =
      thread.customer?.accountStatus || thread.customer?.status || null;
    const customerLastActive = thread.customer?.lastActive || null;
    const customerMemberSince = thread.customer?.memberSince || null;
    const businessAccountStatus =
      thread.business?.accountStatus || thread.business?.status || null;
    const businessVerificationStatus = thread.business?.verificationStatus || null;
    const businessMemberSince = thread.business?.memberSince || null;

    const totalMessageThreads = thread.totalMessageThreads ?? thread.totalMessages ?? 0;
    const totalQuoteThreads = thread.totalQuoteThreads ?? thread.totalMessages ?? 0;
    const totalFlags = getOverviewTotalFlags(thread);
    const startedFromType =
      thread.startedFromType ||
      (String(thread.startedFrom || thread.source || '').toLowerCase().includes('profile')
        ? 'Business Profile'
        : String(thread.startedFrom || thread.source || '').toLowerCase().includes('post')
          ? 'Post'
          : thread.startingPostId || thread.startingPostTitle
            ? 'Post'
            : 'Business Profile');
    const startingPostTitle = thread.startingPostTitle || null;
    const startingPostId = thread.startingPostId || null;
    const startingPostUrl = startingPostId ? `/admin/posts/${startingPostId}` : null;
    const startedOn = thread.startedAt || thread.created || thread.submittedAt;
    const lastMessageOn = thread.lastMessageOn || thread.lastActivity || thread.lastMessage;
    const submittedOn =
      thread.submittedOn || thread.quoteCreated || thread.groupQuoteCreated || thread.startedAt || thread.created || thread.submittedAt;
    const deliveredOn = thread.deliveredOn || null;
    const lastQuoteThreadOn = thread.lastQuoteThreadOn || thread.lastActivity || thread.lastMessage;
    const lastUpdated = thread.lastUpdated || thread.lastActivity || (isQuote ? lastQuoteThreadOn : lastMessageOn);
    const verificationStatusLabel = businessVerificationStatus || 'Not Verified';

    const handleCopyOverviewField = async (fieldKey, value) => {
      if (!value || value === '—') return;
      try {
        await navigator.clipboard.writeText(String(value));
        setCopiedOverviewField(fieldKey);
        window.setTimeout(() => setCopiedOverviewField(null), 1500);
      } catch {
        // ignore clipboard errors
      }
    };

    const renderCopyableOverviewValue = (fieldKey, value, label) => {
      const display = value || '—';
      const canCopy = Boolean(value && value !== '—');
      return (
        <AdminDetailFieldValue as="div" className="inline-flex items-center gap-1.5 min-w-0">
          <span className="truncate" title={display}>{display}</span>
          {canCopy ? (
            <button
              type="button"
              onClick={() => handleCopyOverviewField(fieldKey, value)}
              className="inline-flex items-center justify-center text-mediumgray hover:text-primaryblue shrink-0"
              title={copiedOverviewField === fieldKey ? 'Copied' : `Copy ${label}`}
              aria-label={copiedOverviewField === fieldKey ? 'Copied' : `Copy ${label}`}
            >
              <i
                className={`fi ${
                  copiedOverviewField === fieldKey ? 'fi-rr-check text-freshgreen' : 'fi-rr-copy'
                } text-xs leading-none`}
                aria-hidden
              />
            </button>
          ) : null}
        </AdminDetailFieldValue>
      );
    };

    return (
      <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Column 1 — Message / Quote record */}
            <div className="space-y-4">
              {isMessage ? (
                <>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Message ID</AdminDetailFieldLabel>
                    {renderCopyableOverviewValue('messageId', idValue, 'Message ID')}
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Status</AdminDetailFieldLabel>
                    <AdminStatusBadge status={displayThreadStatus} label={displayThreadStatus} />
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Status Details</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>{statusDetails}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Started From</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>{startedFromType}</AdminDetailFieldValue>
                  </div>
                  {startedFromType === 'Post' ? (
                    <>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Starting Post Title</AdminDetailFieldLabel>
                        <AdminDetailFieldValue>
                          {startingPostUrl && startingPostTitle ? (
                            <AdminLinkAction to={startingPostUrl} title={startingPostTitle}>
                              {startingPostTitle}
                            </AdminLinkAction>
                          ) : (
                            startingPostTitle || '—'
                          )}
                        </AdminDetailFieldValue>
                      </div>
                      <div className="space-y-1">
                        <AdminDetailFieldLabel>Starting Post ID</AdminDetailFieldLabel>
                        {renderCopyableOverviewValue('startingPostId', startingPostId, 'Starting Post ID')}
                      </div>
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Quote ID</AdminDetailFieldLabel>
                    {renderCopyableOverviewValue('quoteId', idValue, 'Quote ID')}
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Quote Request ID</AdminDetailFieldLabel>
                    <AdminDetailFieldValue as="div" className="inline-flex items-center gap-1.5 min-w-0">
                      {quoteRequestId ? (
                        <AdminLinkAction
                          to={`/admin/quotes/requests/${quoteRequestId}`}
                          title={quoteRequestId}
                        >
                          {quoteRequestId}
                        </AdminLinkAction>
                      ) : (
                        <span>—</span>
                      )}
                      {quoteRequestId ? (
                        <button
                          type="button"
                          onClick={() => handleCopyOverviewField('quoteRequestId', quoteRequestId)}
                          className="inline-flex items-center justify-center text-mediumgray hover:text-primaryblue shrink-0"
                          title={copiedOverviewField === 'quoteRequestId' ? 'Copied' : 'Copy Quote Request ID'}
                          aria-label={copiedOverviewField === 'quoteRequestId' ? 'Copied' : 'Copy Quote Request ID'}
                        >
                          <i
                            className={`fi ${
                              copiedOverviewField === 'quoteRequestId'
                                ? 'fi-rr-check text-freshgreen'
                                : 'fi-rr-copy'
                            } text-xs leading-none`}
                            aria-hidden
                          />
                        </button>
                      ) : null}
                    </AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Request Source</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>{requestSource}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Status</AdminDetailFieldLabel>
                    <AdminStatusBadge status={displayQuoteStatus} label={displayQuoteStatus} />
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Status Details</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>{statusDetails}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Quote Request Status</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>{quoteRequestStatus}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Response Status</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>{responseStatus}</AdminDetailFieldValue>
                  </div>
                </>
              )}
            </div>

            {/* Column 2 — Customer */}
            <div className="space-y-4">
              <div className="space-y-1">
                <AdminDetailFieldLabel>Customer</AdminDetailFieldLabel>
                <AdminDetailFieldValue>
                  {customerUrl ? (
                    <AdminLinkAction to={customerUrl} state={{ customer: thread.customer }}>
                      {customerName}
                    </AdminLinkAction>
                  ) : (
                    customerName
                  )}
                </AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Customer ID</AdminDetailFieldLabel>
                {renderCopyableOverviewValue('customerId', customerId, 'Customer ID')}
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Account Status</AdminDetailFieldLabel>
                {customerAccountStatus ? (
                  <AdminStatusBadge status={customerAccountStatus} label={customerAccountStatus} />
                ) : (
                  <AdminDetailFieldValue>—</AdminDetailFieldValue>
                )}
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Last Active</AdminDetailFieldLabel>
                <AdminDetailFieldValue>{formatDateTimeOverview(customerLastActive)}</AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Member Since</AdminDetailFieldLabel>
                <AdminDetailFieldValue>{formatDate(customerMemberSince)}</AdminDetailFieldValue>
              </div>
            </div>

            {/* Column 3 — Business */}
            <div className="space-y-4">
              <div className="space-y-1">
                <AdminDetailFieldLabel>Business</AdminDetailFieldLabel>
                <AdminDetailFieldValue>
                  {businessUrl ? (
                    <AdminLinkAction to={businessUrl} state={{ business: thread.business }}>
                      {businessName}
                    </AdminLinkAction>
                  ) : (
                    businessName
                  )}
                </AdminDetailFieldValue>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Business ID</AdminDetailFieldLabel>
                {renderCopyableOverviewValue('businessId', businessId, 'Business ID')}
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Account Status</AdminDetailFieldLabel>
                {businessAccountStatus ? (
                  <AdminStatusBadge status={businessAccountStatus} label={businessAccountStatus} />
                ) : (
                  <AdminDetailFieldValue>—</AdminDetailFieldValue>
                )}
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Verification Status</AdminDetailFieldLabel>
                <AdminStatusBadge status={verificationStatusLabel} label={verificationStatusLabel} />
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Member Since</AdminDetailFieldLabel>
                <AdminDetailFieldValue>{formatDate(businessMemberSince)}</AdminDetailFieldValue>
              </div>
            </div>

            {/* Column 4 — counts + dates */}
            <div className="space-y-4">
              {isMessage ? (
                <>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Total Message Threads</AdminDetailFieldLabel>
                    <AdminDetailFieldValue className="tabular-nums">{totalMessageThreads}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Total Flags</AdminDetailFieldLabel>
                    <AdminDetailFieldValue className="tabular-nums">{totalFlags}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Started On</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>{formatDateTimeOverview(startedOn)}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Last Message On</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>{formatDateTimeOverview(lastMessageOn)}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Last Updated</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>{formatDateTimeOverview(lastUpdated)}</AdminDetailFieldValue>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Total Quote Threads</AdminDetailFieldLabel>
                    <AdminDetailFieldValue className="tabular-nums">{totalQuoteThreads}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Total Flags</AdminDetailFieldLabel>
                    <AdminDetailFieldValue className="tabular-nums">{totalFlags}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Submitted On</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>{formatDateTimeOverview(submittedOn)}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Delivered On</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>{formatDateTimeOverview(deliveredOn)}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Last Quote Thread On</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>{formatDateTimeOverview(lastQuoteThreadOn)}</AdminDetailFieldValue>
                  </div>
                  <div className="space-y-1">
                    <AdminDetailFieldLabel>Last Updated</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>{formatDateTimeOverview(lastUpdated)}</AdminDetailFieldValue>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
    );
  };

  // Helper function to format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Helper function to check if two dates are on the same day
  const isSameDay = (dateString1, dateString2) => {
    if (!dateString1 || !dateString2) return false;
    const d1 = new Date(dateString1);
    const d2 = new Date(dateString2);
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const grouped = [];
    let currentDate = null;
    
    messages.forEach((message) => {
      if (!currentDate || !isSameDay(currentDate, message.date)) {
        // Add date separator
        grouped.push({
          type: 'date-separator',
          date: message.date,
          formattedDate: formatDateForDisplay(message.date)
        });
        currentDate = message.date;
      }
      grouped.push(message);
    });
    
    return grouped;
  };

  const renderTimeline = () => {
    if (!thread || !thread.threadActivity) return null;
    
    // Check if this is a quote thread
    const type = thread.type || thread.threadType || '';
    const isQuoteThread = type.toLowerCase().includes('quote');
    /** Quote Activity tab keeps rich quote UI; Messages tab (non-quote threads) uses transcript-only updates. */
    const isQuoteActivityTab = isQuoteThread && activeTab === 'quoteThread';

    // Convert threadActivity to message format - show all activities (Message, Quote, etc.)
    let messages = thread.threadActivity
      .filter(activity => activity.type === 'Message' || activity.type === 'Quote' || activity.type === 'QuoteEntry')
      .map(activity => {
        // Determine sender type
        const sender = activity.from.type === 'Customer' ? 'customer' : 
                      activity.from.type === 'Business' ? 'business' : 'customer';
        
        // Parse timestamp from activity.time (format: "23 Oct 2025 14:22")
        let messageDate = new Date();
        if (activity.time) {
          try {
            messageDate = new Date(activity.time);
            if (isNaN(messageDate.getTime())) {
              messageDate = new Date();
            }
          } catch (e) {
            messageDate = new Date();
          }
        }
        
        const isQuote = activity.type === 'Quote' || activity.type === 'QuoteEntry';
        const linkedFlag = (thread.flags || []).find(
          (flag) =>
            flag.messageThreadId === activity.id ||
            flag.quoteThreadId === activity.id
        );
        const deliveryStatus = isQuoteThread
          ? normalizeQuoteThreadDeliveryStatus(activity.messageStatus)
          : normalizeMessageDeliveryStatus(activity.messageStatus);

        return {
          id: activity.id,
          text: activity.content,
          sender: sender,
          timestamp: activity.time,
          date: messageDate.toISOString(),
          avatar: activity.from.avatar || (sender === 'customer' ? thread.customer?.avatar : thread.business?.avatar),
          name: activity.from.name,
          type: isQuote ? 'quote' : 'text',
          attachments: activity.attachments || 0,
          messageStatus: deliveryStatus,
          isFlagged: Boolean(activity.isFlagged || linkedFlag),
          flagId: activity.flagId || linkedFlag?.flagId || null,
          // Quote-specific fields - only show quote data for Quote type activities in quote threads
          quoteDescription: (isQuote && isQuoteThread) ? (activity.quoteDescription || thread.quoteDescription || thread.groupQuoteDescription || '') : null,
          category: (isQuote && isQuoteThread) ? (activity.category || thread.tradeCategory || thread.business?.mainTrade || '') : null,
          quoteAttachments: (isQuote && isQuoteThread) ? (activity.quoteAttachments || thread.quoteAttachments || 0) : 0,
          quoteAttachmentFiles: (isQuote && isQuoteThread) ? (activity.quoteAttachmentFiles || thread.quoteAttachmentFiles || []) : [],
          // Flag to indicate if this message should show quote card - only for quote activities in quote threads
          showQuoteCard: (isQuote && isQuoteThread) && (activity.quoteDescription || thread.quoteDescription || thread.groupQuoteDescription || activity.category || thread.tradeCategory || activity.quoteAttachments || thread.quoteAttachments > 0)
        };
      });

    // Quote Activity only: synthetic quote entry when thread has quote data (restored; not used on Messages tab)
    if (
      isQuoteActivityTab &&
      (thread.quoteDescription || thread.groupQuoteDescription) &&
      !messages.some((m) => m.type === 'quote' && m.quoteDescription)
    ) {
      const quoteCreatedDate = thread.quoteCreated || thread.groupQuoteCreated || thread.startedAt || new Date();
      const quoteTime = formatDateTime(quoteCreatedDate);

      messages.push({
        id: 'quote-entry',
        text: 'Here is the detailed quote for your project.',
        sender: 'business',
        timestamp: quoteTime,
        date: new Date(quoteCreatedDate).toISOString(),
        avatar: thread.business?.avatar || require('../../../assets/img/User.png'),
        name: thread.business?.name || 'Business',
        type: 'quote',
        attachments: 0,
        quoteDescription:
          thread.quoteDescription ||
          thread.groupQuoteDescription ||
          'Complete project quote with all materials, labor, and services included. Estimated completion time: 5-7 days.',
        category: thread.tradeCategory || thread.business?.mainTrade || 'General',
        quoteAttachments: thread.quoteAttachments || 2,
        quoteAttachmentFiles: thread.quoteAttachmentFiles || [
          { name: 'quote_document.pdf', size: '1.2 MB', url: '#' },
          { name: 'material_list.xlsx', size: '456 KB', url: '#' },
        ],
        showQuoteCard: true,
      });
    }

    // Sort by date
    messages = messages.sort((a, b) => new Date(a.date) - new Date(b.date));

    const groupedMessages = groupMessagesByDate(messages);

    return (
      <DashboardMainContainer className="!bg-white">
        <div className="p-0">
          <div className="flex flex-col" style={{ height: ADMIN_CHAT_THREAD_LAYOUT.viewportHeight, minHeight: '500px', maxHeight: '800px' }}>
            {/* Messages Area */}
            <div 
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto space-y-4 p-6 pr-2 slim-scrollbar" 
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#C0C0C0 #F2F2F2'
              }}
            >
              {messages.length === 0 ? (
                <div className="py-10 text-center">
                  <AdminSupportingText>
                    No messages found.
                  </AdminSupportingText>
                </div>
              ) : (
                groupedMessages.map((item, index) => {
                  // Render date separator
                  if (item.type === 'date-separator') {
                    return (
                      <div key={`date-${index}`} className="flex items-center justify-center my-4">
                        <AdminMetadataText className="font-semibold text-offblack">
                          {item.formattedDate}
                        </AdminMetadataText>
                      </div>
                    );
                  }

                  const hasQuoteCardFields = Boolean(
                    item.quoteDescription ||
                      item.category ||
                      item.quoteAttachments > 0 ||
                      (item.quoteAttachmentFiles && item.quoteAttachmentFiles.length > 0)
                  );

                  // Quote Activity tab only: structured quote blocks inside bubbles.
                  const shouldShowQuoteCard = isQuoteActivityTab
                    ? (item.type === 'quote' || item.showQuoteCard) && hasQuoteCardFields
                    : false;

                  if (!shouldShowQuoteCard && (!item.text || !item.text.trim())) {
                    return null;
                  }

                  const isMessageBubble = !isQuoteThread && item.type !== 'quote';
                  const isQuoteBubble = isQuoteThread && item.type !== 'date-separator';
                  const deliveryStatus = item.messageStatus
                    ? (isQuoteThread
                        ? normalizeQuoteThreadDeliveryStatus(item.messageStatus)
                        : normalizeMessageDeliveryStatus(item.messageStatus))
                    : null;
                  // Message Thread IDs stay in data (highlight, flags, keys) — not shown on bubbles.
                  const showBubbleThreadId =
                    isQuoteBubble &&
                    item.id &&
                    !String(item.id).startsWith('act-') &&
                    item.id !== 'quote-entry';
                  const showBubbleMetaRow =
                    (isMessageBubble || isQuoteBubble) && (showBubbleThreadId || item.isFlagged);

                  return (
                    <div
                      key={item.id}
                      id={item.id ? `message-thread-${item.id}` : undefined}
                      className={`flex ${item.sender === "business" ? "justify-end" : "justify-start"} ${
                        highlightedMessageThreadId && item.id === highlightedMessageThreadId
                          ? 'rounded-xl ring-2 ring-primaryblue ring-offset-2'
                          : ''
                      }`}
                    >
                      <div className={ADMIN_CHAT_THREAD_LAYOUT.messageRowClassName(item.sender)}>
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          <img
                            src={item.avatar || require('../../../assets/img/User.png')}
                            alt={item.name}
                            className="w-8 h-8 rounded-full object-cover"
                            onError={(e) => {
                              e.target.src = require('../../../assets/img/User.png');
                            }}
                          />
                        </div>
                        
                        {/* Message / Quote Thread Bubble */}
                        <div className="flex flex-col min-w-0">
                          {showBubbleMetaRow ? (
                            <div
                              className={`mb-1 px-1 flex flex-wrap items-center gap-2 ${
                                item.sender === 'business' ? 'justify-end' : 'justify-start'
                              }`}
                            >
                              {showBubbleThreadId ? (
                                <AdminMetadataText className="text-xs text-mediumgray">
                                  {item.id}
                                </AdminMetadataText>
                              ) : null}
                              {item.isFlagged ? (
                                <button
                                  type="button"
                                  onClick={() => setActiveTab('flags')}
                                  className="inline-flex items-center gap-1 text-alertred hover:opacity-80 bg-transparent border-0 p-0 cursor-pointer"
                                  title={item.flagId ? `Open Flags — ${item.flagId}` : 'Open Flags'}
                                  aria-label={
                                    item.flagId
                                      ? `Flagged ${item.flagId}`
                                      : isQuoteBubble
                                        ? 'Flagged quote thread'
                                        : 'Flagged message'
                                  }
                                >
                                  <i className="fi fi-rr-flag text-xs leading-none" aria-hidden />
                                  <AdminMetadataText className="text-xs text-alertred">
                                    {item.flagId || 'Flagged'}
                                  </AdminMetadataText>
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                          <div
                            className={`${ADMIN_CHAT_THREAD_LAYOUT.bubbleClassName(item.sender)} ${
                              (isMessageBubble || isQuoteBubble) && item.isFlagged
                                ? 'border border-alertred'
                                : ''
                            }`}
                          >
                            {isQuoteThread ? (
                              item.type === "text" &&
                              item.text &&
                              item.text.trim() && (
                                <AdminMainText
                                  className={`${
                                    item.sender === "business" ? "text-white" : "text-offblack"
                                  } leading-relaxed`}
                                >
                                  {item.text}
                                </AdminMainText>
                              )
                            ) : (
                              item.text &&
                              item.text.trim() && (
                                <AdminMainText
                                  className={`${
                                    item.sender === "business" ? "text-white" : "text-offblack"
                                  } leading-relaxed whitespace-pre-wrap`}
                                >
                                  {item.text}
                                </AdminMainText>
                              )
                            )}

                            {/* Quote Activity: structured quote blocks. Messages tab does not use this path for quote threads. */}
                            {isQuoteActivityTab && (item.type === "quote" || shouldShowQuoteCard) && (
                              <div className="space-y-3">
                                {/* Quote intro text */}
                                {item.text && item.text.trim() && item.type === "quote" && (
                                  <AdminMainText
                                    className={`${
                                      item.sender === "business" ? "text-white" : "text-offblack"
                                    } leading-relaxed`}
                                  >
                                    {item.text}
                                  </AdminMainText>
                                )}
                                {/* Message text if available */}
                                {item.text && item.text.trim() && item.type !== "quote" && (
                                  <AdminMainText
                                    className={`${
                                      item.sender === "business" ? "text-white" : "text-offblack"
                                    } leading-relaxed`}
                                  >
                                    {item.text}
                                  </AdminMainText>
                                )}
                                
                                {/* Quote form / Job card */}
                                {item.quoteDescription && item.quoteDescription.trim() && (
                                  <div className={`mt-2 pt-2 border-t ${item.sender === "business" ? "border-blue-300" : "border-gray-300"}`}>
                                    <AdminMetadataText
                                      className={`mb-1 block font-semibold ${
                                        item.sender === "business" ? "text-blue-100" : "text-mediumgray"
                                      }`}
                                    >
                                      Quote form / Job card
                                    </AdminMetadataText>
                                    <AdminMainText
                                      className={`${
                                        item.sender === "business" ? "text-white" : "text-offblack"
                                      } leading-relaxed`}
                                    >
                                      {item.quoteDescription}
                                    </AdminMainText>
                                  </div>
                                )}
                                
                                {/* Category data */}
                                {item.category && item.category.trim() && (
                                  <div className={`mt-2 pt-2 border-t ${item.sender === "business" ? "border-blue-300" : "border-gray-300"}`}>
                                    <AdminMetadataText
                                      className={`mb-1 block font-semibold ${
                                        item.sender === "business" ? "text-blue-100" : "text-mediumgray"
                                      }`}
                                    >
                                      Category
                                    </AdminMetadataText>
                                    <AdminMainText
                                      className={`${
                                        item.sender === "business" ? "text-white" : "text-offblack"
                                      } leading-relaxed`}
                                    >
                                      {item.category}
                                    </AdminMainText>
                                  </div>
                                )}
                                
                                {/* Attachments */}
                                {(item.quoteAttachments > 0 || (item.quoteAttachmentFiles && item.quoteAttachmentFiles.length > 0)) && (
                                  <div className={`mt-2 pt-2 border-t ${item.sender === "business" ? "border-blue-300" : "border-gray-300"}`}>
                                    <AdminMetadataText
                                      className={`mb-1 block font-semibold ${
                                        item.sender === "business" ? "text-blue-100" : "text-mediumgray"
                                      }`}
                                    >
                                      Attachments
                                    </AdminMetadataText>
                                    <div className="space-y-1">
                                      {item.quoteAttachmentFiles && item.quoteAttachmentFiles.length > 0 ? (
                                        item.quoteAttachmentFiles.map((file, idx) => (
                                          <div key={idx} className={`flex items-center gap-2 ${item.sender === "business" ? "text-blue-100" : "text-gray-600"}`}>
                                            <i className="fi fi-rr-paperclip text-xs"></i>
                                            <AdminMetadataText className="text-xs">
                                              {file.name} {file.size ? `(${file.size})` : ''}
                                            </AdminMetadataText>
                                          </div>
                                        ))
                                      ) : (
                                        <div className={`flex items-center gap-2 ${item.sender === "business" ? "text-blue-100" : "text-gray-600"}`}>
                                          <i className="fi fi-rr-paperclip text-xs"></i>
                                          <AdminMetadataText className="text-xs">
                                            {item.quoteAttachments} attachment{item.quoteAttachments > 1 ? 's' : ''}
                                          </AdminMetadataText>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Attachments indicator for regular messages */}
                            {item.type === "text" && item.attachments > 0 && (
                              <div className={`mt-2 flex items-center gap-2 ${item.sender === "business" ? "text-blue-100" : "text-gray-600"}`}>
                                <i className="fi fi-rr-paperclip text-sm"></i>
                                <AdminMetadataText className="text-xs">
                                  {item.attachments} attachment{item.attachments > 1 ? 's' : ''}
                                </AdminMetadataText>
                              </div>
                            )}
                          </div>
                          
                          {/* Timestamp + WhatsApp-style delivery ticks */}
                          <div
                            className={`mt-1 px-2 flex flex-wrap items-center gap-1.5 ${
                              item.sender === 'business' ? 'justify-end' : 'justify-start'
                            }`}
                          >
                            <AdminMetadataText className="text-mediumgray">
                              {item.timestamp}
                            </AdminMetadataText>
                            {(isMessageBubble || isQuoteBubble) && deliveryStatus ? (
                              <MessageDeliveryTicks status={deliveryStatus} />
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </DashboardMainContainer>
    );
  };

  const renderEngagements = () => {
    if (!thread || !thread.engagements) return null;
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
          <AdminSupportingText className="text-offblack mb-2">Avg. Customer Response Time</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{thread.engagements.avgCustomerResponseTime}</AdminMetric>
        </DashboardMainContainer>

        <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
          <AdminSupportingText className="text-offblack mb-2">Avg. Business Response Time</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{thread.engagements.avgBusinessResponseTime}</AdminMetric>
        </DashboardMainContainer>

        <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
          <AdminSupportingText className="text-offblack mb-2">Total Messages</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{thread.engagements.totalMessages}</AdminMetric>
        </DashboardMainContainer>

        <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
          <AdminSupportingText className="text-offblack mb-2">Customer Message Count</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{thread.engagements.customerMessageCount}</AdminMetric>
        </DashboardMainContainer>

        <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
          <AdminSupportingText className="text-offblack mb-2">Business Message Count</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{thread.engagements.businessMessageCount}</AdminMetric>
        </DashboardMainContainer>

        <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
          <AdminSupportingText className="text-offblack mb-2">Conversation Duration</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{thread.engagements.conversationDuration}</AdminMetric>
        </DashboardMainContainer>

        <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
          <AdminSupportingText className="text-offblack mb-2">Attachment Count</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{thread.engagements.attachmentCount}</AdminMetric>
        </DashboardMainContainer>
      </div>
    );
  };

  const renderParticipants = () => (
    <DashboardMainContainer className="!bg-white">
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Customer Column */}
          <div className="space-y-6">
            <AdminPanelTitle className="text-offblack">Customer</AdminPanelTitle>
            <div className="space-y-4">
              <div className="space-y-1">
                <AdminDetailFieldLabel>Name</AdminDetailFieldLabel>
                <div className="text-sm text-offblack font-medium">{thread.customer?.name || '—'}</div>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Customer ID</AdminDetailFieldLabel>
                <div className="text-sm text-offblack font-medium">{thread.customer?.id || '—'}</div>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Status</AdminDetailFieldLabel>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                  thread.customer?.status === 'Active'
                    ? 'bg-freshgreen text-white'
                    : thread.customer?.status === 'Under Review' || thread.customer?.underReview
                    ? 'bg-yellow-500 text-white'
                    : thread.customer?.status === 'Suspended'
                    ? 'bg-orange-500 text-white'
                    : thread.customer?.status === 'Banned' || thread.customer?.status === 'Blocked' || thread.customer?.status === 'Closed'
                    ? 'bg-alertred text-white'
                    : thread.customer?.status === 'Deleted' || thread.customer?.status === 'Inactive'
                    ? 'bg-gray-600 text-white'
                    : 'bg-lightgray text-offblack'
                }`}>
                  {thread.customer?.status === 'Under Review' || (thread.customer?.underReview && !thread.customer?.status) ? 'Under Review' : thread.customer?.status || '—'}
                </span>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Link to Customer Profile</AdminDetailFieldLabel>
                <AdminLinkAction
                  to={thread.customer?.profileUrl || `/admin/users/customers/${thread.customer?.id}`}
                >
                  View profile →
                </AdminLinkAction>
              </div>
            </div>
          </div>

          {/* Business Column */}
          <div className="space-y-6">
            <AdminPanelTitle className="text-offblack">Business</AdminPanelTitle>
            <div className="space-y-4">
              <div className="space-y-1">
                <AdminDetailFieldLabel>Name</AdminDetailFieldLabel>
                <div className="text-sm text-offblack font-medium">{thread.business?.name || '—'}</div>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Business ID</AdminDetailFieldLabel>
                <div className="text-sm text-offblack font-medium">{thread.business?.id || '—'}</div>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Category</AdminDetailFieldLabel>
                <div className="text-sm text-offblack font-medium">{thread.business?.mainTrade || thread.tradeCategory || '—'}</div>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Verification Status</AdminDetailFieldLabel>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                  thread.business?.verified || thread.business?.verificationStatus === 'Verified'
                    ? 'bg-freshgreen text-white'
                    : 'bg-lightgray text-offblack'
                }`}>
                  {thread.business?.verified || thread.business?.verificationStatus === 'Verified' ? 'Verified' : 'Not Verified'}
                </span>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Subscription Plan</AdminDetailFieldLabel>
                <div className="text-sm text-offblack font-medium">{thread.business?.subscriptionPlan || 'Standard'}</div>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Link to Business Profile</AdminDetailFieldLabel>
                <AdminLinkAction
                  to={thread.business?.profileUrl || `/admin/users/businesses/${thread.business?.id}`}
                >
                  View profile →
                </AdminLinkAction>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardMainContainer>
  );

  const renderQuotes = () => {
    if (!thread) return null;
    
    // Build quotes array from thread data
    const quotes = [];
    
    // Add Direct Quote if exists
    if (thread.quoteId) {
      const quoteStatus = normalizeQuoteStatus(thread.quoteStatus, thread.status);
      
      quotes.push({
        id: thread.quoteId,
        quoteId: thread.quoteId,
        type: 'Direct Quote',
        requestedBy: thread.customer?.name || 'Customer',
        recipients: 1,
        respondedBy: quoteStatus === 'Open' ? (thread.business?.name || 'Business') : null,
        status: quoteStatus,
        issuedOn: thread.quoteCreated || thread.startedAt,
        expiryDate: thread.quoteExpiry || null,
        businessId: thread.business?.id
      });
    }
    
    // Add Group Quote if exists
    if (thread.groupQuoteId) {
      const groupQuoteStatus = normalizeQuoteStatus(thread.groupQuoteStatus, thread.status);
      
      const respondedBusinesses = groupQuoteStatus === 'Open' ? ['SwiftFlow Plumbing', 'Rapid Roofers'] : [];
      
      quotes.push({
        id: thread.groupQuoteId,
        quoteId: thread.groupQuoteId,
        type: 'Group Quote',
        requestedBy: thread.customer?.name || 'Customer',
        recipients: thread.groupQuoteInvitedCount || 5,
        respondedBy: respondedBusinesses.length > 0 ? respondedBusinesses.join(', ') : null,
        status: groupQuoteStatus,
        issuedOn: thread.groupQuoteCreated || thread.startedAt,
        expiryDate: null, // Would need expiry date from data
        businessId: null
      });
    }
    
    // Filter quotes based on filters
    let filteredQuotes = quotes;
    
    // Quote Type filter
    if (quotesFilters.type !== 'All') {
      filteredQuotes = filteredQuotes.filter(quote => quote.type === quotesFilters.type);
    }
    
    // Quote Status filter
    if (quotesFilters.status !== 'All') {
      filteredQuotes = filteredQuotes.filter(quote => quote.status === quotesFilters.status);
    }
    
    // Issued Date filter
    if (quotesFilters.issuedDateFrom) {
      const fromDate = new Date(quotesFilters.issuedDateFrom);
      filteredQuotes = filteredQuotes.filter(quote => new Date(quote.issuedOn) >= fromDate);
    }
    if (quotesFilters.issuedDateTo) {
      const toDate = new Date(quotesFilters.issuedDateTo);
      toDate.setHours(23, 59, 59, 999);
      filteredQuotes = filteredQuotes.filter(quote => new Date(quote.issuedOn) <= toDate);
    }
    
    // Toggle quotes row actions
    const toggleQuotesRowActions = (quoteId) => {
      setQuotesRowActionsOpen((prev) => {
        if (prev[quoteId]) return {};
        return { [quoteId]: true };
      });
    };
    
    return (
      <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            {/* Quote Type Filter */}
            <div className="relative" ref={quotesTypeFilterRef}>
              <AdminFilterDropdownButton
                onClick={() => {
                  setQuotesTypeFilterOpen(!quotesTypeFilterOpen);
                  setQuotesStatusFilterOpen(false);
                  setQuotesDateFilterOpen(false);
                }}
              >
                <span>Quote Type: {quotesFilters.type}</span>
                <i
                  className={
                    quotesTypeFilterOpen
                      ? "fi fi-rs-angle-up lg:text-[12px] cursor-pointer flex items-center ml-3"
                      : "fi fi-rs-angle-down lg:text-[12px] cursor-pointer flex items-center ml-3"
                  }
                ></i>
              </AdminFilterDropdownButton>
              {quotesTypeFilterOpen && (
                <div className="flex flex-col rounded w-40 py-2 absolute z-30 bg-white text-offblack mt-3 shadow-lg border border-lightgray">
                  {['All', 'Direct Quote', 'Group Quote'].map((type) => (
                    <button
                      key={type}
                      onClick={() => {
                        setQuotesFilters(prev => ({ ...prev, type }));
                        setQuotesTypeFilterOpen(false);
                      }}
                      className="block px-3 py-1.5 hover:text-primaryblue w-full mt-1 text-left"
                    >
                      <AdminSupportingText>{type}</AdminSupportingText>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Quote Status Filter */}
            <div className="relative" ref={quotesStatusFilterRef}>
              <AdminFilterDropdownButton
                onClick={() => {
                  setQuotesStatusFilterOpen(!quotesStatusFilterOpen);
                  setQuotesTypeFilterOpen(false);
                  setQuotesDateFilterOpen(false);
                }}
              >
                <span>Quote Status: {quotesFilters.status}</span>
                <i
                  className={
                    quotesStatusFilterOpen
                      ? "fi fi-rs-angle-up lg:text-[12px] cursor-pointer flex items-center ml-3"
                      : "fi fi-rs-angle-down lg:text-[12px] cursor-pointer flex items-center ml-3"
                  }
                ></i>
              </AdminFilterDropdownButton>
              {quotesStatusFilterOpen && (
                <div className="flex flex-col rounded w-40 py-2 absolute z-30 bg-white text-offblack mt-3 shadow-lg border border-lightgray">
                  {['All', ...QUOTE_DISPLAY_STATUSES].map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setQuotesFilters(prev => ({ ...prev, status }));
                        setQuotesStatusFilterOpen(false);
                      }}
                      className="block px-3 py-1.5 hover:text-primaryblue w-full mt-1 text-left"
                    >
                      <AdminSupportingText>{status}</AdminSupportingText>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Issued Date Filter */}
            <div className="relative" ref={quotesDateFilterRef}>
              <AdminFilterDropdownButton
                onClick={() => {
                  setQuotesDateFilterOpen(!quotesDateFilterOpen);
                  setQuotesTypeFilterOpen(false);
                  setQuotesStatusFilterOpen(false);
                }}
              >
                <span>Issued Date: {quotesFilters.issuedDateFrom && quotesFilters.issuedDateTo ? `${formatDate(quotesFilters.issuedDateFrom)} → ${formatDate(quotesFilters.issuedDateTo)}` : 'All'}</span>
                <i
                  className={
                    quotesDateFilterOpen
                      ? "fi fi-rs-angle-up lg:text-[12px] cursor-pointer flex items-center ml-3"
                      : "fi fi-rs-angle-down lg:text-[12px] cursor-pointer flex items-center ml-3"
                  }
                ></i>
              </AdminFilterDropdownButton>
              {quotesDateFilterOpen && (
                <div className="flex flex-col rounded w-64 py-2 absolute z-30 bg-white text-offblack mt-3 shadow-lg border border-lightgray">
                  <div className="px-3 py-2 space-y-2">
                    <div>
                      <AdminDetailFieldLabel>From</AdminDetailFieldLabel>
                      <input
                        type="date"
                        value={quotesFilters.issuedDateFrom || ''}
                        onChange={(e) => setQuotesFilters(prev => ({ ...prev, issuedDateFrom: e.target.value }))}
                        className="w-full border border-lightgray rounded px-2 py-1 text-xs"
                      />
                    </div>
                    <div>
                      <AdminDetailFieldLabel>To</AdminDetailFieldLabel>
                      <input
                        type="date"
                        value={quotesFilters.issuedDateTo || ''}
                        onChange={(e) => setQuotesFilters(prev => ({ ...prev, issuedDateTo: e.target.value }))}
                        className="w-full border border-lightgray rounded px-2 py-1 text-xs"
                      />
                    </div>
                    <button
                      onClick={() => {
                        setQuotesFilters(prev => ({ ...prev, issuedDateFrom: null, issuedDateTo: null }));
                        setQuotesDateFilterOpen(false);
                      }}
                      className="w-full mt-2 px-3 py-1.5 text-xs text-primaryblue hover:bg-gray-50 rounded"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quotes Table */}
          <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
            {/* Header */}
            <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2" style={{ gridTemplateColumns: 'minmax(80px, 0.8fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(80px, 0.8fr) minmax(120px, 1.5fr) minmax(90px, 0.9fr) minmax(90px, 0.9fr) minmax(90px, 0.9fr) minmax(50px, 0.5fr)' }}>
                <div className="flex items-center">
                  <span className="font-semibold">Quote ID</span>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold">Quote Type</span>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold">Requested By</span>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold">Recipients</span>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold">Responded By</span>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold">Quote Status</span>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold">Issued On</span>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold">Expiry Date</span>
                </div>
                <div className="flex items-center">
                  <AdminTableHeader>Actions</AdminTableHeader></div>
              </div>
              
              {/* Rows */}
              {filteredQuotes.length > 0 ? filteredQuotes.map((quote) => {
                const quoteId = quote.quoteId || quote.id;
                return (
                  <div key={quoteId} className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2" style={{ gridTemplateColumns: 'minmax(80px, 0.8fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(80px, 0.8fr) minmax(120px, 1.5fr) minmax(90px, 0.9fr) minmax(90px, 0.9fr) minmax(90px, 0.9fr) minmax(50px, 0.5fr)' }}>
                  <div className="flex items-center min-w-0">
                    <button
                      onClick={() => {
                        if (quote.type === 'Direct Quote') {
                          navigate(`/admin/quotes/all/${quoteId}`);
                        } else {
                          navigate(`/admin/quotes/group/${quoteId}`);
                        }
                      }}
                      className="text-offblack hover:text-primaryblue truncate"
                      title={quoteId}
                    >
                      {quoteId}
                    </button>
                  </div>
                  <div className="flex items-center min-w-0">
                    <span className="text-offblack truncate" title={quote.type}>
                      {quote.type}
                    </span>
                  </div>
                  <div className="flex items-center min-w-0">
                    <span className="text-offblack truncate" title={quote.requestedBy || '—'}>
                      {quote.requestedBy || '—'}
                    </span>
                  </div>
                  <div className="flex items-center min-w-0">
                    <span className="text-offblack truncate">
                      {quote.recipients} {quote.recipients === 1 ? 'business' : 'businesses'}
                    </span>
                  </div>
                  <div className="flex items-center min-w-0">
                    <span className="text-offblack truncate" title={quote.respondedBy || '—'}>
                      {quote.respondedBy || '—'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      quote.status === 'Submitted'
                        ? 'bg-warmyellow text-offblack'
                        : quote.status === 'Open'
                        ? 'bg-freshgreen text-white'
                        : quote.status === 'Closed'
                        ? 'bg-mediumgray text-white'
                        : 'bg-lightgray text-offblack'
                    }`}>
                      {quote.status}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-offblack">{formatDate(quote.issuedOn)}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-offblack">{quote.expiryDate ? formatDate(quote.expiryDate) : '—'}</span>
                  </div>
                  <div className="flex items-center relative" onClick={(e) => e.stopPropagation()}>
                    <div className="relative" ref={el => quotesRowActionsRefs.current[quoteId] = el}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleQuotesRowActions(quoteId);
                        }}
                        className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                        title="Row Actions"
                      >
                        <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                      </button>
                      {quotesRowActionsOpen[quoteId] && (
                        <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray" onClick={(e) => e.stopPropagation()}>
                          <div className="py-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // View Quote Request - navigate to quote detail
                                if (quote.type === 'Direct Quote') {
                                  navigate(`/admin/quotes/all/${quoteId}`);
                                } else {
                                  navigate(`/admin/quotes/group/${quoteId}`);
                                }
                                setQuotesRowActionsOpen(prev => ({ ...prev, [quoteId]: false }));
                              }}
                              className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                            >
                              <AdminSupportingText>View Quote Request</AdminSupportingText>
                            </button>
                            {quote.respondedBy && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // View Quote Responses - navigate to quote detail with responses view
                                  if (quote.type === 'Direct Quote') {
                                    navigate(`/admin/quotes/all/${quoteId}?tab=responses`);
                                  } else {
                                    navigate(`/admin/quotes/group/${quoteId}?tab=responses`);
                                  }
                                  setQuotesRowActionsOpen(prev => ({ ...prev, [quoteId]: false }));
                                }}
                                className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                              >
                                <AdminSupportingText>View Quote Responses</AdminSupportingText>
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/admin/users/customers/${thread.customer?.id}`);
                                setQuotesRowActionsOpen(prev => ({ ...prev, [quoteId]: false }));
                              }}
                              className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                            >
                              <AdminSupportingText>Open Customer Profile</AdminSupportingText>
                            </button>
                            {quote.businessId && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/admin/users/businesses/${quote.businessId}`);
                                  setQuotesRowActionsOpen(prev => ({ ...prev, [quoteId]: false }));
                                }}
                                className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                              >
                                <AdminSupportingText>Open Business Profile</AdminSupportingText>
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            }) : (
              <div className="py-8 text-center">
                <span className="text-mediumgray">No quotes found</span>
              </div>
            )}
          </DashboardMainContainer>
      </div>
    );
  };

  const renderModerationAndFlags = () => {
    const moderationCases = thread.moderationCases || [];
    const flags = thread.flags || [];
    const allItems = [
      ...moderationCases.map(item => ({ ...item, isCase: true })),
      ...flags.map(item => ({ ...item, isCase: false }))
    ];

    const severityClasses = {
      Low: 'bg-warmyellow text-white',
      Medium: 'bg-orange-500 text-white',
      High: 'bg-alertred text-white',
      Critical: 'bg-red-700 text-white',
    };

    const statusClasses = {
      'Open': 'bg-warmyellow text-white',
      'Under Review': 'bg-primaryblue text-white',
      'Resolved': 'bg-freshgreen text-white',
      'Closed': 'bg-mediumgray text-white',
      'New': 'bg-warmyellow text-white',
      'Attached': 'bg-primaryblue text-white',
      'Resolved': 'bg-freshgreen text-white',
    };

    return (
      <DashboardMainContainer className="!bg-white">
        <div className="p-6">
          <div className="space-y-4">
            {/* Table Header */}
            <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(90px, 0.9fr) minmax(70px, 0.7fr) minmax(100px, 1fr) minmax(80px, 0.8fr) minmax(95px, 0.9fr) minmax(95px, 0.9fr) minmax(40px, 0.4fr)' }}>
              <div className="flex items-center">
                <span className="font-semibold">ID</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Type</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Status</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Severity</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Object</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Reason / Rule</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Opened / Created On</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Last Updated</span>
              </div>
              <div className="flex items-center">
                <AdminTableHeader>Actions</AdminTableHeader></div>
            </div>

            {/* Table Rows */}
            {allItems.length === 0 ? (
              <div className="py-10 px-4 text-center">
                <AdminMainText className="text-mediumgray">
                  No moderation cases or flags recorded for this conversation.
                </AdminMainText>
              </div>
            ) : (
              allItems.map((item) => {
                const itemId = item.caseId || item.id || item.flagId;
                return (
                  <div
                    key={itemId || Math.random()}
                    className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4"
                    style={{ gridTemplateColumns: 'minmax(100px, 1fr) minmax(120px, 1.2fr) minmax(90px, 0.9fr) minmax(70px, 0.7fr) minmax(100px, 1fr) minmax(80px, 0.8fr) minmax(95px, 0.9fr) minmax(95px, 0.9fr) minmax(40px, 0.4fr)' }}
                  >
                    {/* ID */}
                    <div className="flex items-center min-w-0">
                      <AdminLinkAction
                        type="button"
                        onClick={() => {
                          const caseId = item.linkedCaseId || item.caseId || item.id || itemId;
                          navigate(`/admin/moderation/queue/${caseId}`, {
                            state: {
                              from: location.pathname,
                              item: {
                                caseId,
                                contentType: 'Message',
                                status: item.status || thread?.caseStatus || 'Under Review',
                                severity: item.severity || thread?.caseSeverity,
                                preview: thread?.latestMessagePreview,
                                assignedTo: item.assignedTo || thread?.moderationCases?.[0]?.assignedTo,
                                created: item.created || thread?.moderationCases?.[0]?.created,
                                updated: item.updated || thread?.moderationCases?.[0]?.updated,
                                conversationId: thread?.conversationId,
                                customerName: thread?.customer?.name,
                                businessName: thread?.business?.name,
                              },
                            },
                          });
                        }}
                        className="truncate text-left"
                        title={itemId || '—'}
                      >
                        {itemId || '—'}
                      </AdminLinkAction>
                    </div>

                    {/* Type */}
                    <div className="flex items-center">
                      <span className="">{item.type || '—'}</span>
                    </div>

                    {/* Status */}
                    <div className="flex items-center">
                      {item.status ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusClasses[item.status] || 'bg-mediumgray text-white'}`}>
                          {item.status}
                        </span>
                      ) : (
                        <span className="">—</span>
                      )}
                    </div>

                    {/* Severity */}
                    <div className="flex items-center">
                      {item.severity ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${severityClasses[item.severity] || 'bg-mediumgray text-white'}`}>
                          {item.severity}
                        </span>
                      ) : (
                        <span className="">—</span>
                      )}
                    </div>

                    {/* Object */}
                    <div className="flex items-center min-w-0">
                      <span className="truncate" title={item.object || item.messageNumber || item.quoteId || item.groupQuoteId || 'Full Conversation'}>
                        {item.object || item.messageNumber ? `Message #${item.messageNumber}` : item.quoteId ? `Quote ${item.quoteId}` : item.groupQuoteId ? `Group Quote ${item.groupQuoteId}` : item.attachmentId ? 'Attachment' : 'Full Conversation'}
                      </span>
                    </div>

                    {/* Reason / Rule */}
                    <div className="flex items-center min-w-0">
                      <span className="truncate" title={item.reason || item.ruleCode || '—'}>
                        {item.reason || item.ruleCode || '—'}
                      </span>
                    </div>

                    {/* Opened / Created On */}
                    <div className="flex items-center">
                      <span className="">{item.createdAt || item.openedAt ? formatDate(item.createdAt || item.openedAt) : '—'}</span>
                    </div>

                    {/* Last Updated */}
                    <div className="flex items-center">
                      <span className="">{item.lastUpdated ? formatDate(item.lastUpdated) : '—'}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center relative" onClick={(e) => e.stopPropagation()}>
                      <div className="relative" ref={el => moderationRowActionsRefs.current[itemId] = el}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleModerationRowActions(itemId);
                          }}
                          className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                          title="Row Actions"
                        >
                          <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                        </button>
                        {moderationRowActionsOpen[itemId] && (
                          <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                            <div className="py-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const caseId = item.linkedCaseId || item.caseId || item.id || itemId;
                                  navigate(`/admin/moderation/queue/${caseId}`, {
                                    state: {
                                      from: location.pathname,
                                      item: {
                                        caseId,
                                        contentType: 'Message',
                                        status: item.status || thread?.caseStatus || 'Under Review',
                                        severity: item.severity || thread?.caseSeverity,
                                        preview: thread?.latestMessagePreview,
                                        assignedTo: item.assignedTo || thread?.moderationCases?.[0]?.assignedTo,
                                        created: item.created || thread?.moderationCases?.[0]?.created,
                                        updated: item.updated || thread?.moderationCases?.[0]?.updated,
                                        conversationId: thread?.conversationId,
                                        customerName: thread?.customer?.name,
                                        businessName: thread?.business?.name,
                                      },
                                    },
                                  });
                                  setModerationRowActionsOpen(prev => ({ ...prev, [itemId]: false }));
                                }}
                                className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                              >
                                <AdminSupportingText>View Case</AdminSupportingText>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </DashboardMainContainer>
    );
  };

  const buildThreadFlagRows = (t) => {
    const isQuoteRecord = String(t?.type || t?.threadType || '').toLowerCase().includes('quote');

    const normalizeFlagId = (id, index = 0) => {
      if (!id) {
        const num = (index + 1) * 100 + Math.floor(Math.random() * 99);
        return `FLG-${String(num).padStart(5, '0')}`;
      }
      if (typeof id === 'string' && id.startsWith('FLG-')) return id;
      const match = id.toString().match(/(\d+)/);
      if (match) return `FLG-${String(match[1]).padStart(5, '0')}`;
      const hash = id.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return `FLG-${String((hash % 99999) + 10000).padStart(5, '0')}`;
    };

    const resolveFlaggedBy = (flag) => {
      if (flag.flaggedBy) return flag.flaggedBy;
      const source = String(flag.source || flag.raisedBy || '').toLowerCase();
      if (source.includes('customer')) return t.customer?.name || 'Customer';
      if (source.includes('business')) return t.business?.name || 'Business';
      return '—';
    };

    const resolveBubbleThreadId = (flag) => {
      if (isQuoteRecord) {
        if (flag.quoteThreadId) return flag.quoteThreadId;
        if (flag.messageThreadId && String(flag.messageThreadId).startsWith('QTH-')) {
          return flag.messageThreadId;
        }
        if (flag.threadId && String(flag.threadId).startsWith('QTH-')) return flag.threadId;
        const flaggedActivity = (t.threadActivity || []).find((a) => a.isFlagged);
        return flaggedActivity?.id || null;
      }
      if (flag.messageThreadId) return flag.messageThreadId;
      if (flag.threadId && String(flag.threadId).startsWith('MTH-')) return flag.threadId;
      const flaggedActivity = (t.threadActivity || []).find((a) => a.isFlagged && a.type === 'Message');
      return flaggedActivity?.id || null;
    };

    return (t.flags || []).map((flag, index) => {
      let severity = flag.severity || 'Medium';
      if (severity === 'Critical') severity = 'High';
      if (!['Low', 'Medium', 'High'].includes(severity)) severity = 'Medium';

      const flagId = normalizeFlagId(flag.flagId || flag.id, index);
      const bubbleThreadId = resolveBubbleThreadId(flag);
      const handlingRecord =
        flag.linkedCaseId || flag.handlingRecord || flag.caseId || t.moderationCaseId || null;
      const handlingStatus = handlingRecord
        ? normalizeMessageFlagHandlingStatus(
            flag.handlingStatus ||
              flag.caseStatus ||
              flag.status ||
              flag.flagStatus ||
              t.caseStatus ||
              t.moderationCases?.[0]?.status
          )
        : null;
      const isDecided = handlingStatus === 'Decided';

      return {
        key: flagId,
        flagId,
        messageThreadId: bubbleThreadId,
        quoteThreadId: isQuoteRecord ? bubbleThreadId : null,
        flaggedBy: resolveFlaggedBy(flag),
        reason: flag.reason || flag.trigger || flag.object || '—',
        severity,
        handlingRecord,
        handlingStatus,
        flaggedOn: flag.createdAt || flag.openedOn || flag.created || t.startedAt || t.created,
        linkedCaseId: handlingRecord,
        linkedCaseType: flag.linkedCaseType || (handlingRecord ? 'Moderation' : null),
        actionLabel: handlingRecord ? 'Open Case' : 'View Thread',
        openFromArchive: isDecided,
      };
    });
  };

  const viewFlaggedMessageThread = (messageThreadId) => {
    if (!messageThreadId) return;
    const isQuote = String(thread?.type || thread?.threadType || '').toLowerCase().includes('quote');
    setHighlightedMessageThreadId(messageThreadId);
    setActiveTab(isQuote ? 'quoteThread' : 'timeline');
  };

  const openFlagHandlingCase = (flag) => {
    const caseId = flag.linkedCaseId || flag.handlingRecord;
    if (!caseId) return;
    const caseType = flag.linkedCaseType || 'Moderation';
    const isQuote = String(thread?.type || thread?.threadType || '').toLowerCase().includes('quote');
    const isDecided = flag.openFromArchive || flag.handlingStatus === 'Decided';
    if (caseType === 'Moderation') {
      navigate(`/admin/moderation/queue/${caseId}`, {
        state: {
          from: location.pathname,
          fromArchive: !!isDecided,
          readOnly: !!isDecided,
          item: {
            caseId,
            contentType: isQuote ? 'Quote' : 'Message',
            status: flag.handlingStatus || thread?.caseStatus || 'Active',
            severity: thread?.caseSeverity || flag.severity,
            preview: thread?.latestMessagePreview || thread?.quoteTitle,
            assignedTo: thread?.moderationCases?.[0]?.assignedTo,
            created: thread?.moderationCases?.[0]?.created,
            updated: thread?.moderationCases?.[0]?.updated,
            conversationId: thread?.conversationId || thread?.messageId || thread?.quoteId,
            customerName: thread?.customer?.name,
            businessName: thread?.business?.name,
          },
        },
      });
      return;
    }
    if (caseType === 'Fraud') {
      navigate(`/admin/fraud/cases/${caseId}`);
      return;
    }
    if (caseType === 'Support') {
      navigate(`/admin/support/tickets/${caseId}`);
    }
  };

  const handleMessageFlagRowAction = (flag) => {
    if (flag.actionLabel === 'Open Case' && (flag.linkedCaseId || flag.handlingRecord)) {
      openFlagHandlingCase(flag);
      return;
    }
    viewFlaggedMessageThread(flag.messageThreadId || flag.quoteThreadId);
  };

  const renderFlags = () => {
    if (!thread) return null;

    const isQuote = String(thread.type || thread.threadType || '').toLowerCase().includes('quote');
    const threadIdColumnLabel = isQuote ? 'Quote Thread ID' : 'Message Thread ID';
    const emptyFlagsCopy = isQuote
      ? 'No Flags have been submitted for this Quote.'
      : 'No Flags have been submitted for this Message.';

    const threadFlagRows = buildThreadFlagRows(thread);
    const flagsTablePagination = getDetailTablePagination(threadFlagRows.length, flagsCurrentPage);
    const paginatedFlagRows = flagsTablePagination.paginate(threadFlagRows);

    return (
      <div className="space-y-6 min-w-0 max-w-full">
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div className="w-full min-w-0 max-w-full overflow-x-auto">
            <div className="min-w-[960px]">
              <div
                className="grid min-w-0 py-3 text-offblack border-b border-lightgray gap-x-2 sm:gap-x-3 gap-y-2 px-4"
                style={{ gridTemplateColumns: MESSAGE_FLAGS_TABLE_GRID }}
              >
                <AdminDetailTableHeader className="min-w-0 break-words">Flag ID</AdminDetailTableHeader>
                <AdminDetailTableHeader className="min-w-0 break-words">Case ID</AdminDetailTableHeader>
                <AdminDetailTableHeader className="min-w-0 break-words">{threadIdColumnLabel}</AdminDetailTableHeader>
                <AdminDetailTableHeader className="min-w-0 break-words">Flagged By</AdminDetailTableHeader>
                <AdminDetailTableHeader className="min-w-0 break-words">Reason</AdminDetailTableHeader>
                <AdminDetailTableHeader className="min-w-0 break-words">Severity</AdminDetailTableHeader>
                <AdminDetailTableHeader className="min-w-0 break-words">Case Status</AdminDetailTableHeader>
                <AdminDetailTableHeader className="min-w-0 break-words leading-tight">Flagged On</AdminDetailTableHeader>
                <AdminDetailTableHeader className="min-w-0 break-words">Action</AdminDetailTableHeader>
              </div>

              {paginatedFlagRows.length > 0 ? (
                paginatedFlagRows.map((flag) => (
                  <div
                    key={flag.key}
                    className="grid min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                    style={{ gridTemplateColumns: MESSAGE_FLAGS_TABLE_GRID }}
                  >
                    <AdminTableBody className="min-w-0 break-words leading-snug">{flag.flagId}</AdminTableBody>
                    <div className="flex items-start min-w-0">
                      {flag.handlingRecord ? (
                        <AdminLinkAction
                          type="button"
                          onClick={() => openFlagHandlingCase(flag)}
                          className="min-w-0 break-words break-all leading-snug text-left"
                        >
                          {flag.handlingRecord}
                        </AdminLinkAction>
                      ) : (
                        <AdminTableBody>—</AdminTableBody>
                      )}
                    </div>
                    <div className="flex items-start min-w-0">
                      {flag.messageThreadId ? (
                        <AdminLinkAction
                          type="button"
                          onClick={() => viewFlaggedMessageThread(flag.messageThreadId)}
                          className="min-w-0 break-words break-all leading-snug text-left"
                        >
                          {flag.messageThreadId}
                        </AdminLinkAction>
                      ) : (
                        <AdminTableBody>—</AdminTableBody>
                      )}
                    </div>
                    <AdminTableBody className="min-w-0 break-words leading-snug" title={flag.flaggedBy}>
                      {flag.flaggedBy || '—'}
                    </AdminTableBody>
                    <AdminTableBody className="min-w-0 break-words break-all leading-snug" title={flag.reason}>
                      {flag.reason}
                    </AdminTableBody>
                    <AdminTableBody emphasis className="min-w-0 break-words leading-snug">
                      {flag.severity}
                    </AdminTableBody>
                    <div className="flex items-start min-w-0">
                      {flag.handlingStatus ? (
                        <AdminFlagStatusBadge
                          status={flag.handlingStatus}
                          label={flag.handlingStatus}
                        />
                      ) : (
                        <AdminTableBody>—</AdminTableBody>
                      )}
                    </div>
                    <AdminTableBody className="min-w-0 break-words leading-snug">
                      {formatDateTime(flag.flaggedOn)}
                    </AdminTableBody>
                    <div className="flex items-start min-w-0">
                      {flag.actionLabel ? (
                        <button
                          type="button"
                          className="text-left bg-transparent border-0 p-0 cursor-pointer"
                          onClick={() => handleMessageFlagRowAction(flag)}
                        >
                          <AdminTableBody className="text-offblack underline-offset-2 hover:underline">
                            {flag.actionLabel}
                          </AdminTableBody>
                        </button>
                      ) : (
                        <AdminTableBody>—</AdminTableBody>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-8 text-center">
                  <AdminSupportingText className="text-mediumgray">
                    {emptyFlagsCopy}
                  </AdminSupportingText>
                </div>
              )}
            </div>
          </div>
        </DashboardMainContainer>
        <ConfigurationListPaginationFooter
          show={flagsTablePagination.showPagination}
          rangeStart={flagsTablePagination.rangeStart}
          rangeEnd={flagsTablePagination.rangeEnd}
          totalCount={threadFlagRows.length}
          currentPage={flagsTablePagination.effectivePage}
          totalPages={flagsTablePagination.totalPages}
          onPrev={() => setFlagsCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() =>
            setFlagsCurrentPage((prev) => Math.min(flagsTablePagination.totalPages, prev + 1))
          }
          goToPage={setFlagsCurrentPage}
        />
      </div>
    );
  };

  const renderAuditLog = () => (
    <div className="min-w-0 max-w-full">
      <DashboardMainContainer className={`${ADMIN_DETAIL_TABLE_CLASS} overflow-hidden`}>
        <div className="w-full min-w-0 max-w-full overflow-x-auto">
          <div
            className="grid w-full max-w-full min-w-0 py-3 text-offblack border-b border-lightgray px-4 gap-x-2 sm:gap-x-3 gap-y-2"
            style={{ gridTemplateColumns: AUDIT_LOG_GRID }}
          >
            <div className="flex items-center min-w-0">
              <AdminTableHeader>Date and Time</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader>Event</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader>Details</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader>Related Record</AdminTableHeader>
            </div>
          </div>
          {paginatedAuditRows.length > 0 ? (
            paginatedAuditRows.map((log, idx) => (
              <div
                key={`${log.id || log.changeId || log.event}-${idx}`}
                className="grid w-full max-w-full min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                style={{ gridTemplateColumns: AUDIT_LOG_GRID }}
              >
                <div className="flex items-start min-w-0">
                  <AdminTableMeta className="break-words">
                    {formatAuditDateTime(log.dateTime || log.timestamp)}
                  </AdminTableMeta>
                </div>
                <div className="flex items-start min-w-0">
                  <AdminTableBody className="break-words">{log.event || '—'}</AdminTableBody>
                </div>
                <div className="flex items-start min-w-0">
                  <AdminTableBody className="break-words whitespace-pre-wrap">
                    {log.details || '—'}
                  </AdminTableBody>
                </div>
                <div className="flex items-start min-w-0">
                  <AdminTableBody className="break-words">
                    {log.relatedRecord || threadRef || '—'}
                  </AdminTableBody>
                </div>
              </div>
            ))
          ) : (
            <div className="py-8 text-center">
              <AdminSupportingText className="text-mediumgray">
                No audit log entries
              </AdminSupportingText>
            </div>
          )}
        </div>
      </DashboardMainContainer>
      <ConfigurationListPaginationFooter
        show={auditPagination.showPagination}
        rangeStart={auditPagination.rangeStart}
        rangeEnd={auditPagination.rangeEnd}
        totalCount={filteredAuditLogs.length}
        currentPage={auditPagination.effectivePage}
        totalPages={auditPagination.totalPages}
        onPrev={() => setAuditLogCurrentPage((prev) => Math.max(1, prev - 1))}
        onNext={() =>
          setAuditLogCurrentPage((prev) => Math.min(auditPagination.totalPages, prev + 1))
        }
        goToPage={setAuditLogCurrentPage}
      />
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'timeline':
        return renderTimeline();
      case 'quoteThread':
        return renderTimeline();
      case 'flags':
        return renderFlags();
      case 'logs':
      case 'audit-log':
        return renderAuditLog();
      default:
        return renderOverview();
    }
  };

  // Derive conversation title based on type
  const getConversationTitle = () => {
    const type = thread.type || thread.threadType || '';
    if (type.toLowerCase().includes('direct quote')) {
      return 'Direct Quote';
    } else if (type.toLowerCase().includes('group quote')) {
      return 'Group Quote';
    } else {
      // For Message type, show Customer ↔ Business
      const customerName = thread.customer?.name || 'Customer';
      const businessName = thread.business?.name || 'Business';
      return `${customerName} ↔ ${businessName}`;
    }
  };

  const headerType = thread.type || thread.threadType || '';
  const isQuoteHeader = headerType.toLowerCase().includes('quote');
  const quoteHeaderId = thread.quoteId || thread.threadId || thread.conversationId || '—';

  return (
    <AdminLayout>
      <div className="tp-container-main">
      <div>
        <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
          <AdminPanelTitle className="text-offblack">
            {isQuoteHeader ? `Quote Details — ${quoteHeaderId}` : 'Message Details'}
          </AdminPanelTitle>
        </DashboardMainContainer>

        <DashboardMainContainer className="!bg-white">
          <div className="border-b border-lightgray">
            <div className="flex space-x-8 overflow-x-auto">
              {tabs.filter(tab => {
                const type = thread?.type || thread?.threadType || '';
                const isQuote = type.toLowerCase().includes('quote');

                if (tab.id === 'timeline') {
                  return !isQuote;
                }
                if (tab.id === 'quoteThread') {
                  return isQuote;
                }
                return true;
              }).map((tab) => (
                <AdminTabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </AdminTabButton>
              ))}
            </div>
          </div>

          <div
            className={`${
              activeTab === 'timeline' || activeTab === 'quoteThread'
                ? ADMIN_CHAT_THREAD_LAYOUT.tabContentClassName
                : activeTab === 'flags' || activeTab === 'logs' || activeTab === 'audit-log'
                  ? `${ADMIN_TAB_CONTENT_OFFSET_CLASS} w-full min-w-0`
                  : ADMIN_TAB_CONTENT_CONTAINER_CLASS
            }`}
          >
            {renderActiveTab()}
          </div>
        </DashboardMainContainer>
      </div>
      </div>
    </AdminLayout>
  );
};

export default MessageDetail;

